import React from 'react';
import { Field, NumberInput, TextInput, ComboInput, SelectInput, ArrayEditor, GroupedArrayEditor, PrimaryButton, NextButton, GhostButton, AddButton, Badge, fmt, fyLabel, Modal, ModalSection, Card, InfoTooltip } from './dprFields';
import { SECTORS, subSectorsFor, COMPONENT_CATALOG, UOM_OPTIONS } from './sectorData';
import { previewRepaymentByYear, computeIDCP, monthIndexFromDate } from './dprLoanCalc';
import { RepaymentScheduleTool } from './dprRepaymentSchedule';
import { computeRmProcurementPreview } from './dprRmProcurementCalc';
import { computeSalesEstimatePreview } from './dprSalesEstimateCalc';
import { computeDepreciationPreview, subsidyTranchesByYear } from './dprDepreciationCalc';
import { computeWorkingCapitalPreview } from './dprWorkingCapitalCalc';

export const STEP_GROUPS = [
  {
    group: 'Project Setup', steps: [
      { id: 'profile', label: 'Business Profile' },
      { id: 'promoters', label: 'Promoters / Directors' },
    ]
  },
  {
    group: 'Block Capital', steps: [
      { id: 'land_building', label: 'Land & Building' },
      { id: 'plant_machinery', label: 'Plant & Machinery' },
      { id: 'other_assets', label: 'Other Assets' },
      { id: 'preoperative', label: 'Preoperative Expenses' },
    ]
  },
  // Means of Finance sits right after Block Capital — once the total
  // Application of Funds (Cost of Project) is known, the very next thing a
  // bank DPR states is how it's funded, before getting into operational
  // monthly-expense detail. Matches the real DPR's "Cost of Project & Means
  // of Finance" pairing (see the SBI sample reviewed earlier). Government
  // Subsidy lives here too — it's a source of finance, not a cost, even
  // though it's netted against specific assets.
  {
    group: 'Means of Finance', steps: [
      { id: 'sources_of_finance', label: "Promoter's Contribution" },
      { id: 'term_loan', label: 'Term Loan' },
      { id: 'working_capital', label: 'Working Capital' },
      { id: 'subsidy', label: 'Government Subsidy' },
    ]
  },
  {
    group: 'Operations & Sales', steps: [
      { id: 'raw_material', label: 'Capacity & Raw Material' },
      { id: 'depreciation', label: 'Depreciation' },
      { id: 'man_power', label: 'Man Power' },
      { id: 'direct_expenses', label: 'Direct Expenses' },
      { id: 'indirect_expenses', label: 'Indirect Expenses' },
    ]
  },
];
export const ALL_STEPS = STEP_GROUPS.flatMap(g => g.steps);

function itemsTotal(items) { return (items || []).reduce((s, i) => s + (Number(i.amount) || 0), 0); }
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

// Year 1's Working Capital Margin — whichever method is selected on the
// Working Capital step drives this directly (Traditional/Turnover/MPBF-II/
// MPBF-III each compute their own formula-based margin, "Negotiated Limit"
// books the residual against the CA's own flat proposed limit) — just
// pulls that method's own `promoterMargin`. Mirrors dprEngine.js.
function wcMarginForCostOfProject(caseData) {
  return round2((computeWorkingCapitalPreview(caseData).byYear[0] || {}).promoterMargin || 0);
}

// Bank Finance is entered ONCE per Block Capital category (Land / Building /
// Plant & Machinery / Other Assets) as a Finance % — matching how a bank
// sanction letter actually reads (against the category, never against each
// individual invoice line) — and the ₹ amount auto-computes off that
// category's live total, self-updating as costs change. Replaces the old
// per-line-item "Bank Financed" column.
// Resolution order: (1) a % manually set for this category, (2) a %
// derived from pre-existing per-item `bankFinanced` values (before this was
// moved to category level — read-only derivation, never mutates stored
// data, so an old case doesn't silently lose its figures), (3) the overall
// Bank Finance % target from the promoter's own Margin decision (Sources of
// Finance step) — the real-DPR workflow's starting point ("decide the
// overall split first, then allocate it to each category"), still fully
// overridable per category.
function categoryBankFinancePct(cop, key, overallBankFinancePct) {
  const pctMap = cop.bankFinancePctByCategory || {};
  if (pctMap[key] !== undefined && pctMap[key] !== null && pctMap[key] !== '') return pctMap[key];
  const total = itemsTotal(cop[key]);
  const legacySum = itemsTotal((cop[key] || []).map(i => ({ amount: i.bankFinanced })));
  if (total > 0 && legacySum > 0) return round2((legacySum / total) * 100);
  if (overallBankFinancePct !== undefined && overallBankFinancePct !== null && overallBankFinancePct !== '') return overallBankFinancePct;
  return '';
}
function categoryBankFinanceAmount(cop, key, overallBankFinancePct) {
  const pct = Number(categoryBankFinancePct(cop, key, overallBankFinancePct)) || 0;
  return round2(itemsTotal(cop[key]) * pct / 100);
}

// One "Bank Finance %" input + live-computed amount + margin, shown right
// under a category's asset table.
function BankFinanceRow({ cop, categoryKey, onSetPct, overallBankFinancePct }) {
  const total = itemsTotal(cop[categoryKey]);
  const pct = categoryBankFinancePct(cop, categoryKey, overallBankFinancePct);
  const amount = categoryBankFinanceAmount(cop, categoryKey, overallBankFinancePct);
  const marginPct = pct === '' ? '' : round2(100 - Number(pct));
  if (total <= 0) return null;
  return (
    <div className="flex items-center gap-4 flex-wrap mt-2 mb-5 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40">
      <div className="w-40">
        <Field label="Bank Finance %"><NumberInput value={pct} onChange={onSetPct} placeholder="e.g. 75" /></Field>
      </div>
      <Badge tone="indigo">Bank Finance: ₹{fmt(amount)}</Badge>
      <Badge tone="slate">Promoter Margin: {marginPct === '' ? '—' : `${marginPct}%`} (₹{fmt(round2(total - amount))})</Badge>
      <span className="text-[11px] text-slate-400">of category total ₹{fmt(total)}</span>
    </div>
  );
}

// Shared across the Business Profile / Company Details steps — full legal
// names (not abbreviations like "Pvt. Ltd."), with "Others" opening a free-
// text field for anything not listed (Cooperative Society, Statutory Body,
// etc.) rather than leaving the CA stuck with no matching option.
export const BUSINESS_FORMATION_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'Proprietorship', label: 'Sole Proprietorship' },
  { value: 'Partnership', label: 'Partnership Firm' },
  { value: 'LLP', label: 'Limited Liability Partnership (LLP)' },
  { value: 'Private Limited', label: 'Private Limited Company' },
  { value: 'Public Limited', label: 'Public Limited Company' },
  { value: 'OPC', label: 'One Person Company (OPC)' },
  { value: 'Section 8', label: 'Section 8 Company (Non-Profit)' },
  { value: 'HUF', label: 'Hindu Undivided Family (HUF)' },
  { value: 'Trust', label: 'Trust' },
  { value: 'Society', label: 'Registered Society' },
  { value: 'Cooperative Society', label: 'Cooperative Society' },
  { value: 'Others', label: 'Others' },
];
// Only these formations get a CIN (Companies Act registration) — everything
// else (Trust/Society/Cooperative/HUF/Partnership/Proprietorship/Others)
// gets the generic Registration No. field instead, never both.
const COMPANY_FORMATIONS = new Set(['Private Limited', 'Public Limited', 'OPC', 'Section 8']);
function isCompanyFormation(formation) { return COMPANY_FORMATIONS.has(formation); }

// nextLabel resolves from isLastStep automatically (falls back to an
// explicit nextLabel override if one's passed) — so the "Generate Report"
// label always lands on whichever step is actually last in ALL_STEPS,
// without needing to hunt down and re-hardcode it every time the step order
// changes. Save (navy, save-in-place) and Next (orange, save-and-advance)
// are two distinct, clearly separated actions rather than one button
// silently doing both.
function StepFooter({ stepIndex, onBack, onSave, onNext, isLastStep, nextLabel, saving }) {
  const label = nextLabel || (isLastStep ? 'Generate Report' : 'Next');
  return (
    <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100 dark:border-slate-700">
      <GhostButton onClick={onBack} className={stepIndex === 0 ? 'invisible' : ''}>← Back</GhostButton>
      <div className="flex items-center gap-3">
        <PrimaryButton onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
        <NextButton onClick={onNext} disabled={saving}>{label}</NextButton>
      </div>
    </div>
  );
}

function StepHeader({ title, subtitle, total }) {
  return (
    <div className="flex items-start justify-between mb-5">
      <div>
        <h3 className="text-base font-extrabold text-slate-800 dark:text-slate-100">{title}</h3>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {total !== undefined && <Badge tone="indigo">Total: ₹{Number(total).toLocaleString('en-IN')}</Badge>}
    </div>
  );
}

// Lightweight, always-visible running total of Cost of Project vs Sources of
// Finance — the blueprint's "Sources = Applications" principle, without yet
// rebuilding the engine as a full ledger. Shown on every Block Capital /
// Financing step so the CA sees the funding gap while still building.
function FundingBadge({ caseData }) {
  const cop = caseData.cost_of_project || {};
  const mof = caseData.means_of_finance || {};
  const subsidy = caseData.subsidy_config || {};
  const preopTotal = (cop.preopItems && cop.preopItems.length) ? itemsTotal(cop.preopItems) : Number(cop.preop) || 0;
  const wcMarginYear1ForBadge = wcMarginForCostOfProject(caseData);
  const totalApplication = itemsTotal(cop.landItems) + itemsTotal(cop.buildingItems) + itemsTotal(cop.plantMachineryItems)
    + itemsTotal(cop.otherAssetItems) + preopTotal + (Number(cop.idcp) || 0) + itemsTotal(cop.securityDepositItems) + wcMarginYear1ForBadge;
  const subsidyAmount = subsidy.timing === 'upfront' ? (() => {
    const base = subsidy.eligibleAmount ?? totalApplication;
    let amt = base * ((subsidy.pct || 0) / 100);
    if (subsidy.cap) amt = Math.min(amt, subsidy.cap);
    return subsidy.schemeName ? amt : 0;
  })() : 0;
  const equityTotal = (mof.equitySources && mof.equitySources.length) ? itemsTotal(mof.equitySources) : (Number(mof.promoterContribution) || 0);
  const loanAmount = Number(caseData.term_loan_config?.amount) || 0;
  const totalSources = equityTotal + loanAmount + subsidyAmount;
  const gap = Math.round((totalApplication - totalSources) * 100) / 100;

  // Real-DPR allocation check: a bank sanctions/disburses ONE Term Loan
  // amount — the hard ceiling. The Bank Finance % allocated across Land /
  // Building / Plant & Machinery / Other Assets (BankFinanceRow, on each
  // category's own step) must never add up to more than that sanctioned
  // amount, and any shortfall (not yet allocated to a category) is flagged
  // too — "prepare Cost of Project → decide Promoter Margin/Bank Finance →
  // allocate the Bank Finance to each category, checking nothing is left
  // over or over-committed."
  const overallBankFinancePct = (mof.promoterMarginPct !== undefined && mof.promoterMarginPct !== null && mof.promoterMarginPct !== '')
    ? round2(100 - Number(mof.promoterMarginPct)) : undefined;
  const bankFinanceAllocated = round2(
    ['landItems', 'buildingItems', 'plantMachineryItems', 'otherAssetItems']
      .reduce((s, key) => s + categoryBankFinanceAmount(cop, key, overallBankFinancePct), 0)
  );
  const allocationDiff = round2(loanAmount - bankFinanceAllocated);

  return (
    <div className="mb-5">
      <div className="p-3.5 rounded-xl border flex items-center justify-between text-xs"
        style={gap === 0
          ? { background: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.25)' }
          : { background: 'rgba(255,82,0,0.05)', borderColor: 'rgba(255,82,0,0.25)' }}>
        <div className="flex items-center gap-5">
          <div><span className="text-slate-400">Application of Funds</span> <b className="text-slate-700 dark:text-slate-200">₹{fmt(totalApplication)}</b></div>
          <div><span className="text-slate-400">Sources of Funds</span> <b className="text-slate-700 dark:text-slate-200">₹{fmt(totalSources)}</b></div>
        </div>
        {gap === 0
          ? <Badge tone="emerald">Balanced</Badge>
          : <Badge tone="rose">{gap > 0 ? `Funding Gap: ₹${fmt(gap)}` : `Funding Excess: ₹${fmt(-gap)}`}</Badge>}
      </div>
      {loanAmount > 0 && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs">
          <span className="text-slate-400">Bank Finance Allocation (vs Term Loan Sanctioned ₹{fmt(loanAmount)}):</span>
          {allocationDiff < -0.01
            ? <Badge tone="rose">₹{fmt(bankFinanceAllocated)} allocated — ₹{fmt(-allocationDiff)} over the sanctioned loan, reduce a category's Bank Finance %</Badge>
            : allocationDiff > 0.01
              ? <Badge tone="amber">₹{fmt(bankFinanceAllocated)} of ₹{fmt(loanAmount)} allocated — ₹{fmt(allocationDiff)} still unallocated</Badge>
              : <Badge tone="emerald">Fully allocated — ₹{fmt(bankFinanceAllocated)} matches the sanctioned loan</Badge>}
        </div>
      )}
    </div>
  );
}

const EMPTY_PROMOTER = { name: '', relation: '', designation: '', address: '', dob: '', mobile: '', panNo: '', aadharNo: '', qualification: '', experience: '' };

// One promoter/director shown as a compact identity card — replaces a
// 10-column table row (unreadable at that width) with a name/designation
// summary; the full KYC detail lives in the modal form.
function PromoterCard({ p, onEdit, onRemove }) {
  return (
    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{p.name || 'Unnamed Promoter'}</div>
        <div className="text-xs text-slate-400 truncate">{[p.designation, p.relation, p.mobile].filter(Boolean).join(' · ') || 'No details yet'}</div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button type="button" onClick={onEdit} className="text-xs font-bold text-[#0f1f45] dark:text-orange-300 hover:text-[#ff5200]">Edit</button>
        <button type="button" onClick={onRemove} className="text-xs font-bold text-rose-500 hover:text-rose-600">Remove</button>
      </div>
    </div>
  );
}

// Promoters/Directors — a real structured form (sectioned: Personal,
// Identification, Contact & Address, Professional Background) opened in a
// modal per "+ Add Promoter / Director", rather than a cramped inline table
// row — the KYC detail here (10 fields) genuinely needs the room.
function PromoterEditor({ promoters, onChange }) {
  const [modalIndex, setModalIndex] = React.useState(null); // null = closed, -1 = new, >=0 = editing that index
  const [draft, setDraft] = React.useState(EMPTY_PROMOTER);

  const openNew = () => { setDraft(EMPTY_PROMOTER); setModalIndex(-1); };
  const openEdit = (idx) => { setDraft(promoters[idx]); setModalIndex(idx); };
  const close = () => setModalIndex(null);
  const save = () => {
    const next = (promoters || []).slice();
    if (modalIndex === -1) next.push(draft); else next[modalIndex] = draft;
    onChange(next);
    close();
  };
  const remove = (idx) => onChange((promoters || []).filter((_, i) => i !== idx));
  const setField = (key, v) => setDraft(d => ({ ...d, [key]: v }));

  return (
    <div>
      <div className="space-y-2 mb-3">
        {(promoters || []).map((p, idx) => (
          <PromoterCard key={idx} p={p} onEdit={() => openEdit(idx)} onRemove={() => remove(idx)} />
        ))}
      </div>
      <AddButton onClick={openNew}>Add Promoter / Director</AddButton>

      <Modal open={modalIndex !== null} onClose={close}
        title={modalIndex === -1 ? 'Add Promoter / Director' : 'Edit Promoter / Director'}
        subtitle="Printed as the DPR's KYC table for the bank">
        <ModalSection title="Personal Details">
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Full Name" required><TextInput value={draft.name} onChange={v => setField('name', v)} /></Field>
            <Field label="Designation" hint="e.g. Director, Partner, Proprietor"><TextInput value={draft.designation} onChange={v => setField('designation', v)} /></Field>
            <Field label="S/o, D/o or W/o"><TextInput value={draft.relation} onChange={v => setField('relation', v)} /></Field>
            <Field label="Date of Birth"><TextInput value={draft.dob} onChange={v => setField('dob', v)} placeholder="DD/MM/YYYY" /></Field>
          </div>
        </ModalSection>
        <ModalSection title="Identification">
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="PAN"><TextInput value={draft.panNo} onChange={v => setField('panNo', v)} /></Field>
            <Field label="Aadhar No."><TextInput value={draft.aadharNo} onChange={v => setField('aadharNo', v)} /></Field>
          </div>
        </ModalSection>
        <ModalSection title="Contact & Address">
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Mobile No."><TextInput value={draft.mobile} onChange={v => setField('mobile', v)} /></Field>
            <Field label="Address"><TextInput value={draft.address} onChange={v => setField('address', v)} /></Field>
          </div>
        </ModalSection>
        <ModalSection title="Professional Background">
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Qualification"><TextInput value={draft.qualification} onChange={v => setField('qualification', v)} /></Field>
            <Field label="Experience"><TextInput value={draft.experience} onChange={v => setField('experience', v)} placeholder="e.g. 12 years in construction" /></Field>
          </div>
        </ModalSection>
        <div className="flex items-center justify-end gap-3 mt-2 pt-4 border-t border-slate-100 dark:border-slate-700">
          <GhostButton onClick={close}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={!draft.name}>{modalIndex === -1 ? 'Add Promoter' : 'Save Changes'}</PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}

const EMPTY_BLOCK = { name: '', openingGross: undefined, openingDep: 0, rate: undefined, method: 'wdv', idcpShare: undefined, subsidyShare: undefined };

// Depreciation blocks always display Land → Building → Plant & Machinery →
// Other Assets first (the fixed Block Capital order), regardless of the
// order they were auto-created/edited in; any manually-added block (no
// sourceKey) falls after those four, in the order it was added.
const DEPRECIATION_BLOCK_ORDER = { landItems: 0, buildingItems: 1, plantMachineryItems: 2, otherAssetItems: 3 };
const depBlockRank = (b) => DEPRECIATION_BLOCK_ORDER[b.sourceKey] ?? 4;

// One depreciation block shown as a compact card — the 7-column table this
// replaces was as cramped as the old Promoters table; the full detail lives
// in the modal form instead.
function DepreciationBlockCard({ b, onEdit, onRemove }) {
  const nonDep = b.sourceKey === 'landItems';
  return (
    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate flex items-center gap-1.5">
          {b.name || 'Unnamed Block'}
          {b.auto && <Badge tone="indigo">Auto</Badge>}
          {nonDep && <Badge tone="slate">Non-Depreciable</Badge>}
        </div>
        <div className="text-xs text-slate-400 truncate">
          {[b.openingGross ? `Investment ₹${fmt(b.openingGross)}` : null, !nonDep && b.rate !== undefined && `${b.rate}% ${b.method === 'slm' ? 'SLM' : 'WDV'}`,
            b.idcpShare ? `IDCP ₹${fmt(b.idcpShare)}` : null, b.subsidyShare ? `Subsidy Reduction ₹${fmt(b.subsidyShare)}` : null].filter(Boolean).join(' · ') || 'No details yet'}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button type="button" onClick={onEdit} className="text-xs font-bold text-[#0f1f45] dark:text-orange-300 hover:text-[#ff5200]">Edit</button>
        {!b.auto && <button type="button" onClick={onRemove} className="text-xs font-bold text-rose-500 hover:text-rose-600">Remove</button>}
      </div>
    </div>
  );
}

// Depreciation Blocks — a sectioned modal form ("+ Add Block" per the usual
// pattern), rather than a 7-column inline table row.
function DepreciationBlockEditor({ blocks, onChange, extraActions }) {
  const [modalIndex, setModalIndex] = React.useState(null); // null = closed, -1 = new, >=0 = editing that index
  const [draft, setDraft] = React.useState(EMPTY_BLOCK);

  const openNew = () => { setDraft(EMPTY_BLOCK); setModalIndex(-1); };
  const openEdit = (idx) => { setDraft(blocks[idx]); setModalIndex(idx); };
  const close = () => setModalIndex(null);
  const save = () => {
    const next = (blocks || []).slice();
    // "Rate of Depreciation %" is now one single field — taxRate mirrors it
    // (rather than asking for a separate Income Tax Act rate the form had
    // no real room for), kept internally only so nothing downstream that
    // still reads a book/tax split breaks.
    const finalDraft = { ...draft, taxRate: draft.rate };
    if (modalIndex === -1) next.push(finalDraft); else next[modalIndex] = finalDraft;
    onChange(next);
    close();
  };
  const remove = (idx) => onChange((blocks || []).filter((_, i) => i !== idx));
  const setField = (key, v) => setDraft(d => ({ ...d, [key]: v }));
  const nonDep = draft.sourceKey === 'landItems';

  return (
    <div>
      <div className="space-y-2 mb-3">
        {(blocks || []).map((b, idx) => ({ b, idx }))
          .sort((x, y) => depBlockRank(x.b) - depBlockRank(y.b) || x.idx - y.idx)
          .map(({ b, idx }) => (
            <DepreciationBlockCard key={idx} b={b} onEdit={() => openEdit(idx)} onRemove={() => remove(idx)} />
          ))}
      </div>
      <div className="flex items-center justify-between">
        <AddButton onClick={openNew}>Add Block</AddButton>
        {extraActions}
      </div>

      <Modal open={modalIndex !== null} onClose={close}
        title={modalIndex === -1 ? 'Add Depreciation Block' : 'Edit Depreciation Block'}
        subtitle="One block per depreciation-rate category — e.g. Plant & Machinery, Building, Furniture & Fixtures">
        {draft.auto && (
          <div className="mb-3 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg px-3.5 py-2.5 leading-relaxed">
            Auto-created from your Cost of Project entries — Name, Investment Amount, IDCP Share and Subsidy Share stay in sync automatically and can't be hand-edited here.{nonDep ? ' Land never depreciates — Rate stays locked at 0%.' : ' Set the Rate and Method yourself; everything else updates live if you change the underlying cost items.'}
          </div>
        )}
        <ModalSection title="Block Details">
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Block Name" required>
              {draft.auto
                ? <div className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 text-sm text-slate-500 dark:text-slate-400 flex items-center">{draft.name}</div>
                : <ComboInput value={draft.name} onChange={v => setField('name', v)} options={['Plant & Machinery', 'Building & Civil Works', 'Furniture & Fixtures', 'Vehicles', 'Computers & Office Equipment']} />}
            </Field>
            <Field label="Investment Amount" hint={draft.auto ? 'Sum of the matching Cost of Project section' : 'Total cost of this block'}>
              {draft.auto
                ? <div className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 text-sm text-slate-500 dark:text-slate-400 flex items-center">₹{fmt(draft.openingGross)}</div>
                : <NumberInput value={draft.openingGross} onChange={v => setField('openingGross', v)} />}
            </Field>
          </div>
        </ModalSection>
        {!nonDep && (
          <ModalSection title="Depreciation Rate">
            <div className="grid grid-cols-2 gap-x-4">
              <Field label="Rate of Depreciation %" required><NumberInput value={draft.rate} onChange={v => setField('rate', v)} /></Field>
              <Field label="Method">
                <SelectInput value={draft.method || 'wdv'} onChange={v => setField('method', v)} options={[
                  { value: 'wdv', label: 'WDV — Written Down Value' }, { value: 'slm', label: 'SLM — Straight Line Method' },
                ]} />
              </Field>
            </div>
          </ModalSection>
        )}
        {nonDep && (
          <div className="mb-3 text-xs text-slate-400 bg-slate-50 dark:bg-slate-900/40 rounded-lg px-3.5 py-2.5">
            Land is a non-depreciable asset — it stays on the Balance Sheet at cost, with no depreciation charge, no Rate/Method to set.
          </div>
        )}
        <ModalSection title="Capitalisation">
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="IDCP Share (Yr 1)" hint={draft.auto ? 'Auto-split between Building & Plant/Machinery in ratio of investment' : 'Portion of the auto-calculated IDCP capitalised onto this block'}>
              {draft.auto
                ? <div className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 text-sm text-slate-500 dark:text-slate-400 flex items-center">₹{fmt(draft.idcpShare || 0)}</div>
                : <NumberInput value={draft.idcpShare} onChange={v => setField('idcpShare', v)} />}
            </Field>
            <Field label="Subsidy Reduction (Asset Reduction method)" hint={draft.auto ? 'Auto-split the same way, only when Subsidy is marked Granted on the Subsidy step' : 'Only if using the Asset Reduction AS-12 method'}>
              {draft.auto
                ? <div className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 text-sm text-slate-500 dark:text-slate-400 flex items-center">₹{fmt(draft.subsidyShare || 0)}</div>
                : <NumberInput value={draft.subsidyShare} onChange={v => setField('subsidyShare', v)} />}
            </Field>
          </div>
        </ModalSection>
        <div className="flex items-center justify-end gap-3 mt-2 pt-4 border-t border-slate-100 dark:border-slate-700">
          <GhostButton onClick={close}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={!draft.name || (!nonDep && draft.rate === undefined)}>{modalIndex === -1 ? 'Add Block' : 'Save Changes'}</PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}

// The four cost-of-project heads a subsidy scheme actually draws its
// "Eligible Amount" base from — a real scheme usually excludes some line
// items (e.g. Preoperative fees are often ineligible even when P&M is
// fully eligible), so a flat % of the whole project overstates it. Each
// item's qualification (Q/N) is stored directly on the item itself
// (item.subsidyQualified), not a separate lookup — it travels with the item
// through every other edit (rename, remove, reorder) with nothing to keep in
// sync by hand.
const SUBSIDY_ASSET_SECTIONS = [
  { key: 'landItems', label: 'Land & Building — Land' },
  { key: 'buildingItems', label: 'Land & Building — Building & Civil Works' },
  { key: 'plantMachineryItems', label: 'Plant & Machinery' },
  { key: 'otherAssetItems', label: 'Other Assets' },
  { key: 'preopItems', label: 'Preoperative Expenses' },
];

function qualifiedEligibleAmount(cop) {
  return SUBSIDY_ASSET_SECTIONS.reduce((total, s) =>
    total + (cop[s.key] || []).reduce((sub, item) => sub + (item.subsidyQualified ? (Number(item.amount) || 0) : 0), 0), 0);
}

// Trigger is a small circular orange icon button — same size/weight as the
// InfoTooltip "?" it sits beside in the Field's label row, not a full pill
// button, since this is a convenience shortcut rather than a primary action.
// It never silently overwrites Eligible Amount — that only happens when the
// CA explicitly clicks "Use This Total," so a manually-typed figure never
// gets clobbered by just opening the popup.
function SubsidyAssetSelector({ cop, onChangeSection, onApply }) {
  const [open, setOpen] = React.useState(false);
  const qualifiedTotal = qualifiedEligibleAmount(cop);

  const setQualified = (sectionKey, idx, val) => {
    const rows = (cop[sectionKey] || []).slice();
    rows[idx] = { ...rows[idx], subsidyQualified: val };
    onChangeSection(sectionKey, rows);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Select which assets qualify"
        className="w-4 h-4 rounded-full bg-[#ff5200] text-white flex items-center justify-center hover:bg-[#e04800] transition-colors">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Subsidy-Qualified Assets"
        subtitle="Not every cost head qualifies under a given scheme — mark each line item Q (Qualified) or N (Not Qualified). Eligible Amount is the sum of everything marked Q."
        maxWidth="800px">
        {SUBSIDY_ASSET_SECTIONS.map(s => {
          const rows = cop[s.key] || [];
          if (!rows.length) return null;
          return (
            <ModalSection key={s.key} title={s.label}>
              <div className="space-y-1.5">
                {rows.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900/40">
                    <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{item.name || 'Unnamed item'}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-slate-400">₹{fmt(item.amount)}</span>
                      <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                        <button type="button" onClick={() => setQualified(s.key, idx, true)}
                          className={`w-7 h-7 text-[11px] font-extrabold transition-colors ${item.subsidyQualified === true ? 'bg-[#ff5200] text-white' : 'bg-white dark:bg-slate-800 text-slate-400 hover:bg-orange-500/10'}`}>Q</button>
                        <button type="button" onClick={() => setQualified(s.key, idx, false)}
                          className={`w-7 h-7 text-[11px] font-extrabold transition-colors border-l border-slate-200 dark:border-slate-700 ${item.subsidyQualified === false ? 'bg-slate-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>N</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ModalSection>
          );
        })}
        <div className="flex items-center justify-between mt-2 pt-4 border-t border-slate-100 dark:border-slate-700">
          <Badge tone="indigo">Qualified Total: ₹{fmt(qualifiedTotal)}</Badge>
          <div className="flex items-center gap-3">
            <GhostButton onClick={() => setOpen(false)}>Close</GhostButton>
            <PrimaryButton variant="orange" onClick={() => { onApply(qualifiedTotal); setOpen(false); }}>Use This Total</PrimaryButton>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default function CaseWizard({ caseData, setCaseData, stepId, onNext, onBack, onSave, saving, isLastStep }) {
  const set = (field, value) => setCaseData(c => ({ ...c, [field]: value }));
  const setNested = (field, key, value) => setCaseData(c => ({ ...c, [field]: { ...(c[field] || {}), [key]: value } }));

  const bp = caseData.business_profile || {};
  const cop = caseData.cost_of_project || {};
  const mof = caseData.means_of_finance || {};
  const subsidy = caseData.subsidy_config || {};
  const cap = caseData.capacity_config || {};
  const rm = caseData.raw_material || {};
  const rawMaterialNames = (rm.recipeLines || []).map(l => l.name).filter(Boolean);
  const sales = caseData.sales_config || {};
  const labour = caseData.labour_config || {};
  const power = caseData.power_config || {};
  const indirect = caseData.indirect_expenses || {};
  const land = caseData.land_details || {};
  const fa = caseData.fixed_assets || {};
  const tl = caseData.term_loan_config || {};
  const wc = caseData.wc_config || {};

  const stepIndex = ALL_STEPS.findIndex(s => s.id === stepId);
  const goNext = onNext;
  const goBack = onBack;
  const goSave = onSave;
  const [previewOpen, setPreviewOpen] = React.useState(true);
  const [tranchesOpen, setTranchesOpen] = React.useState(true);
  const [annualCapacityOpen, setAnnualCapacityOpen] = React.useState(true);
  const [operatingMonthsOpen, setOperatingMonthsOpen] = React.useState(true);
  const [calcCapacityOpen, setCalcCapacityOpen] = React.useState(true);
  const [outputCalcOpen, setOutputCalcOpen] = React.useState(false);
  const [rmProcurementOpen, setRmProcurementOpen] = React.useState(false);
  const [salesEstimateOpen, setSalesEstimateOpen] = React.useState(false);
  const [depreciationChartOpen, setDepreciationChartOpen] = React.useState(false);
  const [procurementCostUnit, setProcurementCostUnit] = React.useState('actual');
  const [salesValueUnit, setSalesValueUnit] = React.useState('actual');
  const [factoryWorkersOpen, setFactoryWorkersOpen] = React.useState(true);
  const [adminStaffOpen, setAdminStaffOpen] = React.useState(true);
  const [knownOutputPerDay, setKnownOutputPerDay] = React.useState(null);
  const [capacityTab, setCapacityTab] = React.useState('capacity');

  // IDCP is purely a function of the term loan's own fields (moratorium
  // interest) — auto-computed here and kept in sync on cost_of_project.idcp
  // whenever the loan changes, rather than asking the CA to compute and
  // re-type it by hand every time the loan terms move. Interest Subvention's
  // discounted rate feeds in too, since that's the rate actually charged
  // during the moratorium.
  const computedIdcp = computeIDCP(
    subsidy.type === 'interest_subvention'
      ? { ...tl, ratePct: Math.max((tl.ratePct || 0) - (subsidy.pct || 0), 0) }
      : tl
  );
  React.useEffect(() => {
    if (cop.idcp !== computedIdcp) {
      setCaseData(c => ({ ...c, cost_of_project: { ...(c.cost_of_project || {}), idcp: computedIdcp } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedIdcp]);

  // Installment-wise disbursement: the Loan Amount field is hidden (the
  // tranches themselves define it) — keep term_loan_config.amount in sync as
  // their sum, since every other consumer of the loan (Means of Finance
  // totals, FundingBadge, the engine itself) reads that one field as the
  // authoritative total.
  const tranchesTotal = itemsTotal(tl.disbursementTranches);
  React.useEffect(() => {
    if (tl.disbursementType === 'installment_wise' && tl.amount !== tranchesTotal) {
      setNested('term_loan_config', 'amount', tranchesTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tl.disbursementType, tranchesTotal]);

  const preopTotalVal = (cop.preopItems && cop.preopItems.length) ? itemsTotal(cop.preopItems) : (Number(cop.preop) || 0);
  const idcpAllocated = itemsTotal(fa.blocks?.map(b => ({ amount: b.idcpShare })) || []);
  const idcpTotal = Number(cop.idcp) || 0;
  const idcpRemaining = Math.round((idcpTotal - idcpAllocated) * 100) / 100;
  const subsidyAllocated = itemsTotal(fa.blocks?.map(b => ({ amount: b.subsidyShare })) || []);
  const subsidyAmountEst = (() => {
    if (!subsidy.schemeName) return 0;
    const totalApplication = itemsTotal(cop.landItems) + itemsTotal(cop.buildingItems) + itemsTotal(cop.plantMachineryItems) + itemsTotal(cop.otherAssetItems);
    const base = subsidy.eligibleAmount ?? totalApplication;
    let amt = base * ((subsidy.pct || 0) / 100);
    if (subsidy.cap) amt = Math.min(amt, subsidy.cap);
    return Math.round(amt * 100) / 100;
  })();
  const subsidyRemaining = Math.round((subsidyAmountEst - subsidyAllocated) * 100) / 100;
  const subsidyDisbursedTotal = itemsTotal(subsidy.disbursements);
  const disbursedRemaining = Math.round((subsidyAmountEst - subsidyDisbursedTotal) * 100) / 100;

  // Depreciation Blocks auto-created from the Block Capital steps' own cost
  // entries — Land, Building, Plant & Machinery and Other Assets each become
  // a block automatically, with Investment Amount kept in sync to that
  // section's total, so the CA never re-types a figure already entered.
  // Land is included too (fetched, so it shows on the Balance Sheet's Gross
  // Block) but locked at Rate 0% — non-depreciable, never charged. IDCP and
  // — only when the CA marks the subsidy Granted (for reporting) under the
  // Asset Reduction method — the subsidy's asset reduction both auto-split
  // between Building and Plant & Machinery in the ratio of their own
  // investment values; Land and Other Assets get neither (matches how
  // IDCP/subsidy conventionally attach to the core plant-and-building block,
  // not land or ancillary equipment).
  const landTotal = itemsTotal(cop.landItems);
  const buildingTotal = itemsTotal(cop.buildingItems);
  const pmTotal = itemsTotal(cop.plantMachineryItems);
  const otherAssetsTotal = itemsTotal(cop.otherAssetItems);
  const buildingPmTotal = buildingTotal + pmTotal;
  const buildingRatio = buildingPmTotal > 0 ? buildingTotal / buildingPmTotal : 0;
  const subsidyGrantedForAssets = !!subsidy.grantedForAssetReporting && subsidy.accountingMethod === 'asset_reduction';
  const subsidyForAssets = subsidyGrantedForAssets ? subsidyAmountEst : 0;
  const idcpForBuilding = Math.round(computedIdcp * buildingRatio * 100) / 100;
  const idcpForPM = Math.round((computedIdcp - idcpForBuilding) * 100) / 100;
  const subsidyForBuilding = Math.round(subsidyForAssets * buildingRatio * 100) / 100;
  const subsidyForPM = Math.round((subsidyForAssets - subsidyForBuilding) * 100) / 100;
  // Real per-year check: each disbursement tranche reduces Building/P&M only
  // in the actual Financial Year its own date falls into (not lumped into
  // Year 1), capped cumulatively at subsidyAmountEst — same logic that
  // drives the Depreciation Chart, surfaced here too so the CA sees the
  // year-wise split right where the disbursement dates are entered.
  const subsidyTranchesForBadges = subsidyGrantedForAssets ? subsidyTranchesByYear(caseData, subsidyAmountEst) : {};
  // Every block's Investment Amount, auto + manual — the reconciliation
  // check on the Depreciation step compares this against the Block Capital
  // total below, so a CA can see at a glance that nothing was missed.
  const depreciationBlocksTotal = itemsTotal(fa.blocks?.map(b => ({ amount: b.openingGross })) || []);
  const blockCapitalTotal = landTotal + buildingTotal + pmTotal + otherAssetsTotal;

  // Real-DPR funding workflow: a bank sanctions a Term Loan against the
  // depreciable Block Capital (Land/Building/Plant & Machinery/Other
  // Assets) — NOT against IDCP, Pre-Operative Expenses, Security Deposits or
  // Working Capital Margin. Those are funded entirely out of the promoter's
  // own margin (0% bank-financed by default) — confirmed by the CA's own
  // reference DPR, whose Cost of Project table shows ₹0.00 Bank Term Loan
  // against every one of those rows, only Building & Plant/Machinery
  // actually carry a bank-financed figure. So the Bank Finance % decision
  // applies to Block Capital ONLY; the Promoter Margin absorbs the
  // remainder of Block Capital plus 100% of everything else. This also
  // removes what looked like circularity — IDCP depends on the Loan Amount,
  // but the Loan Amount target no longer depends on IDCP (it's a pure
  // function of Block Capital, which doesn't move), so there's nothing to
  // solve for iteratively; the target is stable the moment Block Capital
  // and the Margin % are set.
  const securityDepositsTotal = itemsTotal(cop.securityDepositItems);
  // Year 1's Working Capital Margin — like IDCP/Pre-Op/Security Deposits, a
  // genuine Cost of Project line (injected once, upfront, to seed the
  // operating cycle), computed from whichever WC method is selected.
  const wcMarginYear1 = wcMarginForCostOfProject(caseData);
  const costOfProjectTotal = round2(blockCapitalTotal + preopTotalVal + idcpTotal + securityDepositsTotal + wcMarginYear1);
  const promoterMarginPct = (mof.promoterMarginPct !== undefined && mof.promoterMarginPct !== null && mof.promoterMarginPct !== '') ? Number(mof.promoterMarginPct) : '';
  const overallBankFinancePct = promoterMarginPct === '' ? undefined : round2(100 - promoterMarginPct);
  const bankFinanceTargetAmount = overallBankFinancePct === undefined ? 0 : round2(blockCapitalTotal * overallBankFinancePct / 100);
  const promoterMarginTargetAmount = promoterMarginPct === '' ? 0 : round2(costOfProjectTotal - bankFinanceTargetAmount);

  // Pulled out of the effect below so a manual "Refresh" click can force the
  // exact same sync on demand — a guaranteed fix regardless of whether the
  // reactive effect has fired yet for this session (e.g. blocks were first
  // created before Land items existed, then Land was added afterward).
  const syncDepreciationBlocks = React.useCallback((currentCaseData) => {
    const c = currentCaseData;
    const copNow = c.cost_of_project || {};
    const faNow = c.fixed_assets || {};
    const landT = itemsTotal(copNow.landItems);
    const buildingT = itemsTotal(copNow.buildingItems);
    const pmT = itemsTotal(copNow.plantMachineryItems);
    const otherT = itemsTotal(copNow.otherAssetItems);
    const bpmT = buildingT + pmT;
    const bRatio = bpmT > 0 ? buildingT / bpmT : 0;
    const subsidyNow = c.subsidy_config || {};
    const grantedNow = !!subsidyNow.grantedForAssetReporting && subsidyNow.accountingMethod === 'asset_reduction';
    const subsidyForAssetsNow = grantedNow ? subsidyAmountEst : 0; // subsidyAmountEst itself already tracks live inputs
    const idcpBNow = Math.round(computedIdcp * bRatio * 100) / 100;
    const idcpPNow = Math.round((computedIdcp - idcpBNow) * 100) / 100;
    const subsidyBNow = Math.round(subsidyForAssetsNow * bRatio * 100) / 100;
    const subsidyPNow = Math.round((subsidyForAssetsNow - subsidyBNow) * 100) / 100;

    const AUTO_SECTIONS = [
      { sourceKey: 'landItems', name: 'Land', total: landT, idcpShare: 0, subsidyShare: 0 },
      { sourceKey: 'buildingItems', name: 'Building & Civil Works', total: buildingT, idcpShare: idcpBNow, subsidyShare: subsidyBNow },
      { sourceKey: 'plantMachineryItems', name: 'Plant & Machinery', total: pmT, idcpShare: idcpPNow, subsidyShare: subsidyPNow },
      { sourceKey: 'otherAssetItems', name: 'Other Assets', total: otherT, idcpShare: 0, subsidyShare: 0 },
    ];
    let next = (faNow.blocks || []).slice();
    let changed = false;
    AUTO_SECTIONS.forEach(sec => {
      if (sec.total <= 0) return;
      const idx = next.findIndex(b => b.sourceKey === sec.sourceKey);
      const forcedRate = sec.sourceKey === 'landItems' ? { rate: 0, taxRate: 0 } : {};
      if (idx === -1) {
        next.push({ name: sec.name, sourceKey: sec.sourceKey, auto: true, openingGross: sec.total, openingDep: 0, method: 'wdv', idcpShare: sec.idcpShare, subsidyShare: sec.subsidyShare, ...forcedRate });
        changed = true;
      } else if (next[idx].openingGross !== sec.total || next[idx].idcpShare !== sec.idcpShare || next[idx].subsidyShare !== sec.subsidyShare || next[idx].name !== sec.name
        || (sec.sourceKey === 'landItems' && next[idx].rate !== 0)) {
        next[idx] = { ...next[idx], name: sec.name, openingGross: sec.total, idcpShare: sec.idcpShare, subsidyShare: sec.subsidyShare, ...forcedRate };
        changed = true;
      }
    });
    return changed ? { ...c, fixed_assets: { ...faNow, blocks: next } } : c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedIdcp, subsidyAmountEst]);

  React.useEffect(() => {
    setCaseData(c => syncDepreciationBlocks(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landTotal, buildingTotal, pmTotal, otherAssetsTotal, idcpForBuilding, idcpForPM, subsidyForBuilding, subsidyForPM]);
  const effectiveRatePct = subsidy.type === 'interest_subvention' ? Math.max((tl.ratePct || 0) - (subsidy.pct || 0), 0) : null;
  const equitySourcesTotal = (mof.equitySources && mof.equitySources.length) ? itemsTotal(mof.equitySources) : (Number(mof.promoterContribution) || 0);
  // Each Capital Subsidy disbursement tranche prepays the term loan as a
  // lump sum the month it lands — same resolution the engine does
  // server-side (computeSubsidy) — mirrored here so the wizard's live
  // preview and Repayment Schedule popup show the "Repayment by Subsidy"
  // hit without waiting for a save.
  const subsidyPrepayments = (subsidy.type !== 'interest_subvention' && (subsidy.disbursements || []).length)
    ? subsidy.disbursements.map(d => ({ month: monthIndexFromDate(tl.disbursementDate, d.date), amount: Number(d.amount) || 0 }))
    : [];
  // Interest Subvention doesn't prepay anything — instead the loan's own
  // rate is discounted for every downstream preview/schedule calculation.
  const effectiveTl = subsidy.type === 'interest_subvention'
    ? { ...tl, ratePct: effectiveRatePct }
    : tl;
  const coveragePct = (land.totalAreaSqMt && land.coveredAreaSqMt) ? Math.round((land.coveredAreaSqMt / land.totalAreaSqMt) * 1000) / 10 : null;
  const coverageTone = coveragePct === null ? 'slate' : coveragePct > 80 ? 'rose' : coveragePct > 60 ? 'amber' : 'emerald';

  // Live capacity roll-up — mirrors dprEngine.js's installedAnnualQty formula
  // exactly (per-hour × shift-adjusted hours/day × working days/year, net of
  // wastage) so what's shown here while filling the form is the same figure
  // the engine will actually use, not just an illustrative estimate.
  const capPerDay = (cap.installedCapacityPerHour || 0) * (cap.hoursPerDay || 0) * (cap.shiftsPerDay || 1);
  const capAnnualGross = capPerDay * (cap.workingDaysPerYear || 0);
  const capAnnualNet = capAnnualGross * (1 - (cap.wastagePct || 0) / 100);
  const capUnit = cap.capacityUnit || 'unit';
  // Most industries run all 12 months; a seasonal business (rice bran/paddy
  // extraction, sugar milling) whose raw material is only available part of
  // the year sets its actual season length here — every year's consumption
  // preview below scales against this, matching dprEngine.js exactly, not a
  // hardcoded 12.
  const normalOperatingMonths = cap.normalOperatingMonths || 12;
  const yearMonths = (i) => cap.useOperatingMonths
    ? ((cap.operatingMonthsByYear || [])[i] ?? normalOperatingMonths)
    : normalOperatingMonths;
  const rampUpYears = (cap.rampUpPctByYear && cap.rampUpPctByYear.length) ? cap.rampUpPctByYear : [60];
  const addCapacityYear = () => {
    const arr = [...rampUpYears, rampUpYears[rampUpYears.length - 1] ?? 80];
    setNested('capacity_config', 'rampUpPctByYear', arr);
  };
  const removeCapacityYear = (i) => {
    const arr = rampUpYears.filter((_, ri) => ri !== i);
    const months = (cap.operatingMonthsByYear || []).filter((_, ri) => ri !== i);
    set('capacity_config', { ...cap, rampUpPctByYear: arr, operatingMonthsByYear: months });
  };
  const setCapacityYearPct = (i, v) => {
    const arr = rampUpYears.slice(); arr[i] = v;
    setNested('capacity_config', 'rampUpPctByYear', arr);
  };
  const setOperatingMonth = (i, v) => {
    const arr = (cap.operatingMonthsByYear || []).slice();
    arr[i] = v;
    setNested('capacity_config', 'operatingMonthsByYear', arr);
  };
  // The Annual Capacity table's row count is the single source of truth for
  // how many years this DPR projects — every other schedule (Raw Material
  // Procurement, P&L, Balance Sheet, Cash Flow) reads caseData.projection_years,
  // so keep it locked to that row count rather than letting it drift out of
  // sync as a separately-editable number. Runs on every render where the
  // count actually changed (add/remove a year, or a case loaded with stale
  // data), not just the button clicks, so it can't silently fall out of sync.
  const capacityYearCount = (cap.rampUpPctByYear || []).length;
  React.useEffect(() => {
    if (capacityYearCount > 0 && caseData.projection_years !== capacityYearCount) {
      set('projection_years', capacityYearCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityYearCount]);

  // Bank Financed used to be a per-item column here — moved to one Finance %
  // per category (BankFinanceRow, rendered under each table below), matching
  // how a bank sanction actually reads. See categoryBankFinancePct.
  const assetColumns = (extra = []) => [
    { key: 'name', label: 'Item' }, { key: 'amount', label: 'Amount', type: 'number' }, ...extra,
  ];
  const setBankFinancePct = (categoryKey, v) => {
    set('cost_of_project', { ...cop, bankFinancePctByCategory: { ...(cop.bankFinancePctByCategory || {}), [categoryKey]: v } });
  };

  return (
    <div className="w-full">
      {stepId === 'profile' && (
        <div>
          <StepHeader title="Business Profile" subtitle="Printed on the cover of your project report — keep it short and accurate." />
          <div className="max-w-xs mb-5">
            <Field label="Project Start Financial Year" required tooltip="e.g. 2026 for FY 2026-27 — every year table in this DPR (Capacity, P&L, Balance Sheet, Repayment…) is labeled off this, set once at the very start">
              <NumberInput value={bp.baseFinancialYear} onChange={v => setNested('business_profile', 'baseFinancialYear', v)} placeholder="e.g. 2026" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-x-5">
            <Field label="Business Name" required><TextInput value={caseData.applicant_name} onChange={v => set('applicant_name', v)} /></Field>
            <Field label="Business Formation">
              <SelectInput value={bp.businessFormation} onChange={v => setNested('business_profile', 'businessFormation', v)} options={BUSINESS_FORMATION_OPTIONS} />
            </Field>
            {bp.businessFormation === 'Others' && (
              <Field label="Specify Business Formation"><TextInput value={bp.businessFormationOther} onChange={v => setNested('business_profile', 'businessFormationOther', v)} placeholder="e.g. Cooperative Society" /></Field>
            )}
            <Field label="Project Title" required><TextInput value={caseData.project_title} onChange={v => set('project_title', v)} /></Field>
            <Field label="Description"><TextInput value={bp.description} onChange={v => setNested('business_profile', 'description', v)} /></Field>
            <Field label="Business Status">
              <SelectInput value={bp.businessStatus} onChange={v => setNested('business_profile', 'businessStatus', v)} options={[
                { value: '', label: 'Select…' }, { value: 'New Unit', label: 'New Unit' }, { value: 'Existing — Expansion', label: 'Existing — Expansion' }, { value: 'Existing — Renewal', label: 'Existing — Renewal' },
              ]} />
            </Field>
            <Field label="Project Location"><TextInput value={bp.location} onChange={v => setNested('business_profile', 'location', v)} placeholder="e.g. India" /></Field>
            <Field label="Product Selling Market">
              <SelectInput value={bp.sellingMarket} onChange={v => setNested('business_profile', 'sellingMarket', v)} options={[
                { value: 'domestic', label: 'Domestic' }, { value: 'export', label: 'Export' }, { value: 'both', label: 'Domestic + Export' },
              ]} />
            </Field>
            <Field label="Main Category (Sector)">
              <ComboInput value={bp.sector} onChange={v => set('business_profile', { ...bp, sector: v, subSector: '' })} options={SECTORS} placeholder="Search sector…" />
            </Field>
            <Field label="Sub Category">
              <ComboInput value={bp.subSector} onChange={v => setNested('business_profile', 'subSector', v)} options={subSectorsFor(bp.sector)} placeholder="Search sub-category…" />
            </Field>
            <Field label="Product Segment"><TextInput value={bp.productSegment} onChange={v => setNested('business_profile', 'productSegment', v)} placeholder="e.g. Antibiotics" /></Field>
            <Field label="Contact No"><TextInput value={bp.contactNo} onChange={v => setNested('business_profile', 'contactNo', v)} /></Field>
            <Field label="Email ID"><TextInput value={bp.emailId} onChange={v => setNested('business_profile', 'emailId', v)} /></Field>
            <Field label="Registered / Head Office Address with Zipcode"><TextInput value={bp.address} onChange={v => setNested('business_profile', 'address', v)} /></Field>
          </div>

          <div className="mt-6 mb-1 text-xs font-bold text-slate-500 uppercase tracking-wide">Unit / Factory Details</div>
          <p className="text-xs text-slate-400 mb-2">The registered/head office above is often different from where the plant actually sits — and an existing client may already run other units besides the one this DPR is for. Add every unit relevant to this project.</p>
          <ArrayEditor rows={bp.units} nameCatalog={['Unit I', 'Unit II', 'Unit III', 'Registered Office cum Factory']}
            columns={[{ key: 'name', label: 'Unit Name' }, { key: 'address', label: 'Unit / Factory Address' }]}
            onChange={rows => setNested('business_profile', 'units', rows)} addLabel="+ Add Unit Details" />

          <div className="mt-6 mb-2 text-xs font-bold text-slate-500 uppercase tracking-wide">Statutory Details</div>
          <div className="grid grid-cols-4 gap-x-5">
            <Field label="Business PAN No" required><TextInput value={bp.panNo} onChange={v => setNested('business_profile', 'panNo', v)} /></Field>
            <Field label="GSTIN Status">
              <SelectInput value={bp.gstRegistered} onChange={v => setNested('business_profile', 'gstRegistered', v)} options={[
                { value: '', label: 'Select…' }, { value: 'yes', label: 'Registered' }, { value: 'no', label: 'Not Registered' },
              ]} />
            </Field>
            {bp.gstRegistered === 'yes' && (
              <Field label="GSTIN"><TextInput value={bp.gstNo} onChange={v => setNested('business_profile', 'gstNo', v)} /></Field>
            )}
            {isCompanyFormation(bp.businessFormation) && (
              <Field label="CIN"><TextInput value={bp.cinNo} onChange={v => setNested('business_profile', 'cinNo', v)} /></Field>
            )}
            {bp.businessFormation && !isCompanyFormation(bp.businessFormation) && (
              <Field label="Registration No. (Trust / Society / Firm)"><TextInput value={bp.otherRegistrationNo} onChange={v => setNested('business_profile', 'otherRegistrationNo', v)} /></Field>
            )}
            <Field label="Date of Incorporation / Registration"><TextInput value={bp.incorporationDate} onChange={v => setNested('business_profile', 'incorporationDate', v)} placeholder="DD/MM/YYYY" /></Field>
          </div>

          <div className="mt-6 mb-1 text-xs font-bold text-slate-500 uppercase tracking-wide">Factory / Licence Records</div>
          <p className="text-xs text-slate-400 mb-2">UDYAM, LEI, Pollution NOC, Fire NOC, APMC, FSSAI, Electric Load — add whichever apply, or type your own.</p>
          <ArrayEditor rows={bp.statutoryRecords} nameCatalog={['UDYAM Registration', 'LEI Code', 'Pollution Control Board NOC', 'Fire Department NOC', 'APMC Licence', 'FSSAI Licence', 'Electric Load Sanction']}
            columns={[{ key: 'name', label: 'Record' }, { key: 'number', label: 'Registration No. / Status' }]}
            onChange={rows => setNested('business_profile', 'statutoryRecords', rows)} addLabel="+ Add Statutory Record" />

          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'promoters' && (
        <div>
          <StepHeader title="Promoters / Directors" subtitle="Every promoter, director or partner — printed as the DPR's KYC table for the bank" />
          <PromoterEditor promoters={bp.promoters} onChange={rows => setNested('business_profile', 'promoters', rows)} />
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'land_building' && (
        <div>
          <StepHeader title="Land & Building" subtitle="Freehold land and civil/construction cost — currency in your chosen consistent unit." total={itemsTotal(cop.landItems) + itemsTotal(cop.buildingItems)} />
          <FundingBadge caseData={caseData} />

          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Land Particulars</div>
          <div className="grid grid-cols-4 gap-x-5 mb-2">
            <Field label="Total Land Area (Sq. Mt.)" tooltip="The full size of the plot/site the promoter owns or has leased.">
              <NumberInput value={land.totalAreaSqMt} onChange={v => setNested('land_details', 'totalAreaSqMt', v)} />
            </Field>
            <Field label="Covered / Built-up Area (Sq. Mt.)" tooltip="The portion of that land actually built upon — factory shed, office block, utilities. Always less than or equal to the total land area.">
              <NumberInput value={land.coveredAreaSqMt} onChange={v => setNested('land_details', 'coveredAreaSqMt', v)} />
            </Field>
            <Field label="Land Use Type">
              <SelectInput value={land.landUseType} onChange={v => setNested('land_details', 'landUseType', v)} options={[
                { value: '', label: 'Select…' }, { value: 'Industrial', label: 'Industrial' }, { value: 'Commercial', label: 'Commercial' },
                { value: 'Agricultural', label: 'Agricultural' }, { value: 'Residential', label: 'Residential' },
              ]} />
            </Field>
            <Field label="Type of Construction"><TextInput value={land.constructionType} onChange={v => setNested('land_details', 'constructionType', v)} placeholder="e.g. RCC and MS Iron Structure" /></Field>
            <Field label="Ownership Status">
              <SelectInput value={land.ownershipStatus} onChange={v => setNested('land_details', 'ownershipStatus', v)} options={[
                { value: '', label: 'Select…' }, { value: 'Owned', label: 'Owned' }, { value: 'Leasehold', label: 'Leasehold' }, { value: 'To be Acquired', label: 'To be Acquired' },
              ]} />
            </Field>
            <Field label="Present Status"><TextInput value={land.presentStatus} onChange={v => setNested('land_details', 'presentStatus', v)} placeholder="e.g. Already acquired, converted to non-agricultural" /></Field>
          </div>
          {coveragePct !== null && (
            <div className="mb-5">
              <Badge tone={coverageTone}>Coverage Ratio: {coveragePct}% built-up{coveragePct > 80 ? ' — check local FAR/coverage limits' : ''}</Badge>
            </div>
          )}

          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Land (non-depreciating)</div>
          <ArrayEditor rows={cop.landItems} nameCatalog={['Freehold Land', 'Leasehold Land']}
            columns={assetColumns()}
            onChange={rows => set('cost_of_project', { ...cop, landItems: rows })} addLabel="+ Add Land Item" />
          <BankFinanceRow cop={cop} categoryKey="landItems" overallBankFinancePct={overallBankFinancePct} onSetPct={v => setBankFinancePct('landItems', v)} />
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 mt-5">Building & Civil Works</div>
          <ArrayEditor rows={cop.buildingItems} nameCatalog={COMPONENT_CATALOG.land_building}
            columns={assetColumns()}
            onChange={rows => set('cost_of_project', { ...cop, buildingItems: rows })} addLabel="+ Add Component" />
          <BankFinanceRow cop={cop} categoryKey="buildingItems" overallBankFinancePct={overallBankFinancePct} onSetPct={v => setBankFinancePct('buildingItems', v)} />

          <Card className="p-4 mt-6 bg-slate-50/60 dark:bg-slate-900/40">
            <div className="text-[11px] font-extrabold text-[#001254] dark:text-slate-200 uppercase tracking-wide mb-2.5">Why Land Area & Covered Area both matter</div>
            <ol className="space-y-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed list-decimal list-inside">
              <li><b className="text-slate-700 dark:text-slate-200">Building cost estimation</b> — the Building &amp; Civil Works line above is normally costed as ₹/sq.mt × Covered Area, not total land area. A bank or appraiser sanity-checks your civil cost figure against this ratio.</li>
              <li><b className="text-slate-700 dark:text-slate-200">FAR / coverage-ratio compliance</b> — local municipal and industrial-area bylaws cap what % of a plot can be built on. Banks and Pollution/Fire departments check this before granting NOCs — coverage above ~80% on industrial land is usually a red flag.</li>
              <li><b className="text-slate-700 dark:text-slate-200">Room for expansion</b> — unused land beyond the covered area signals capacity for a future phase, relevant if a bigger sanctioned limit is likely down the line.</li>
              <li><b className="text-slate-700 dark:text-slate-200">Required on real licence/NOC forms</b> — Pollution Control, Fire NOC and industrial registration applications ask for both figures separately, so capturing them here avoids re-deriving them later.</li>
            </ol>
          </Card>

          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'plant_machinery' && (
        <div>
          <StepHeader title="Plant & Machinery" subtitle="Manufacturing equipment only — enter machinery names, cost and how much of each is bank-financed." total={itemsTotal(cop.plantMachineryItems)} />
          <FundingBadge caseData={caseData} />
          <ArrayEditor rows={cop.plantMachineryItems} nameCatalog={COMPONENT_CATALOG.plant_machinery}
            columns={assetColumns()}
            onChange={rows => set('cost_of_project', { ...cop, plantMachineryItems: rows })} addLabel="+ Add Component" />
          <BankFinanceRow cop={cop} categoryKey="plantMachineryItems" overallBankFinancePct={overallBankFinancePct} onSetPct={v => setBankFinancePct('plantMachineryItems', v)} />
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'other_assets' && (
        <div>
          <StepHeader title="Other Assets & Accessories" subtitle="Furniture, computers, safety equipment and anything else physical, not on rent." total={itemsTotal(cop.otherAssetItems)} />
          <FundingBadge caseData={caseData} />
          <ArrayEditor rows={cop.otherAssetItems} nameCatalog={COMPONENT_CATALOG.other_assets}
            columns={assetColumns()}
            onChange={rows => set('cost_of_project', { ...cop, otherAssetItems: rows })} addLabel="+ Add Component" />
          <BankFinanceRow cop={cop} categoryKey="otherAssetItems" overallBankFinancePct={overallBankFinancePct} onSetPct={v => setBankFinancePct('otherAssetItems', v)} />
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'preoperative' && (
        <div>
          <StepHeader title="Preoperative Expenses" subtitle="Setup costs incurred before commercial production — licenses, legal/CA fees, know-how, trial runs" total={preopTotalVal} />
          <FundingBadge caseData={caseData} />

          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Security Deposits</div>
          <p className="text-xs text-slate-400 mb-2">Electricity connection, water connection, telephone/utility deposits, rent security — refundable deposits paid to set up the project, part of Cost of Project but never bank-financed (100% promoter-funded, same as IDCP).</p>
          <ArrayEditor rows={cop.securityDepositItems} nameCatalog={['Electricity Connection Deposit', 'Water Connection Deposit', 'Telephone/Internet Deposit', 'Rent Security Deposit']}
            columns={[{ key: 'name', label: 'Item' }, { key: 'amount', label: 'Amount', type: 'number' }]}
            onChange={rows => set('cost_of_project', { ...cop, securityDepositItems: rows })} addLabel="+ Add Security Deposit" />

          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 mt-5">Preoperative Expenses</div>
          <ArrayEditor rows={cop.preopItems} nameCatalog={COMPONENT_CATALOG.preoperative}
            columns={[{ key: 'name', label: 'Item' }, { key: 'amount', label: 'Amount', type: 'number' }]}
            onChange={rows => set('cost_of_project', { ...cop, preopItems: rows })} addLabel="+ Add Custom Expense" />

          <div className="mt-6 mb-1 text-xs font-bold text-slate-500 uppercase tracking-wide">IDCP — Interest During Construction Period</div>
          <p className="text-xs text-slate-400 mb-3">Interest accrued on the term loan during the moratorium — capitalised into asset cost, not expensed. Bifurcate the total across depreciation blocks in the Loan & Working Capital step; each block's share is set there as "IDCP Share".</p>
          <div className="max-w-sm mb-2">
            <Field label="Total IDCP" tooltip="Purely calculative — the interest that accrues on the term loan during its moratorium. Fill in the loan details on the Loan & Working Capital step and this updates automatically; nothing to type here.">
              <div className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm text-slate-700 dark:text-slate-200">
                ₹{fmt(cop.idcp)}
              </div>
            </Field>
          </div>
          {!tl.moratoriumMonths && (
            <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 mb-3 max-w-md">
              No moratorium set yet — fill in the loan amount, rate, tenor and moratorium on the <b>Loan & Working Capital</b> step and IDCP will auto-calculate here.
            </p>
          )}
          {idcpTotal > 0 && (
            <Badge tone={idcpRemaining === 0 ? 'emerald' : 'amber'}>
              Auto-split onto Building &amp; Plant/Machinery: ₹{fmt(idcpAllocated)} of ₹{fmt(idcpTotal)}{idcpRemaining !== 0 ? ` — ₹${fmt(idcpRemaining)} not yet reflected, save and reopen` : ''}
            </Badge>
          )}
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'subsidy' && (
        <div>
          <StepHeader title="Government Subsidy" subtitle="Optional — leave Scheme Name blank if this project has no subsidy" />
          <FundingBadge caseData={caseData} />

          <Field label="Scheme Name" hint="e.g. MSME, PMEGP, PMFME"><TextInput value={subsidy.schemeName} onChange={v => setNested('subsidy_config', 'schemeName', v)} /></Field>

          <div className="mt-1 mb-2 text-xs font-bold text-slate-500 uppercase tracking-wide">Subsidy Type</div>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${(subsidy.type || 'capital') === 'capital' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
              <input type="radio" className="hidden" checked={(subsidy.type || 'capital') === 'capital'}
                onChange={() => setNested('subsidy_config', 'type', 'capital')} />
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Capital Subsidy</div>
              <div className="text-[11px] text-slate-400 leading-relaxed">Cash that directly reduces the loan — each disbursement prepays the Term Loan the month it lands.</div>
            </label>
            <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${subsidy.type === 'interest_subvention' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
              <input type="radio" className="hidden" checked={subsidy.type === 'interest_subvention'}
                onChange={() => setNested('subsidy_config', 'type', 'interest_subvention')} />
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Interest Subvention</div>
              <div className="text-[11px] text-slate-400 leading-relaxed">No cash changes hands — the bank charges a lower effective rate on the whole loan instead.</div>
            </label>
          </div>

          <div className="grid grid-cols-3 gap-x-5">
            <Field label={subsidy.type === 'interest_subvention' ? 'Interest Subvention %' : 'Subsidy %'}>
              <NumberInput value={subsidy.pct} onChange={v => setNested('subsidy_config', 'pct', v)} />
            </Field>
            {subsidy.type !== 'interest_subvention' && (
              <>
                <Field label="Eligible Amount (base for %)"
                  tooltip="Not every cost head qualifies under a scheme — some assets are commonly excluded even when others are fully eligible. Type the figure directly, or use the picker to sum up whichever line items you mark Qualified."
                  action={<SubsidyAssetSelector cop={cop} onChangeSection={(key, rows) => set('cost_of_project', { ...cop, [key]: rows })}
                    onApply={total => setNested('subsidy_config', 'eligibleAmount', total)} />}>
                  <NumberInput value={subsidy.eligibleAmount} onChange={v => setNested('subsidy_config', 'eligibleAmount', v)} />
                </Field>
                <Field label="Upper Cap Limit" tooltip="The maximum subsidy amount allowed under this scheme's actual policy — regardless of what % × Eligible Amount computes to, the subsidy never exceeds this.">
                  <NumberInput value={subsidy.cap} onChange={v => setNested('subsidy_config', 'cap', v)} />
                </Field>
              </>
            )}
          </div>

          {subsidy.type === 'interest_subvention' ? (
            <Badge tone={effectiveRatePct !== null ? 'emerald' : 'slate'}>
              New Effective Rate: {tl.ratePct || 0}% − {subsidy.pct || 0}% = {fmt(effectiveRatePct)}% — applied to the whole loan
            </Badge>
          ) : (
            <>
              <div className="mt-2 mb-2 text-xs font-bold text-slate-500 uppercase tracking-wide">Accounting Treatment — AS-12 <span className="font-normal normal-case text-slate-400">(Balance Sheet &amp; asset presentation only — doesn't change how the cash itself is used)</span></div>
              <div className="grid grid-cols-3 gap-4">
                <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${(subsidy.accountingMethod || 'capital_reserve') === 'capital_reserve' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
                  <input type="radio" className="hidden" checked={(subsidy.accountingMethod || 'capital_reserve') === 'capital_reserve'}
                    onChange={() => setNested('subsidy_config', 'accountingMethod', 'capital_reserve')} />
                  <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Capital Reserve Method</div>
                  <div className="text-[11px] text-slate-400 leading-relaxed">Credited directly to a Capital Reserve on the Balance Sheet. Never hits P&L — full depreciation is charged on the asset's full cost. AS-12 treatment when the subsidy isn't tied to a specific depreciable asset.</div>
                </label>
                <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${subsidy.accountingMethod === 'deferred_income' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
                  <input type="radio" className="hidden" checked={subsidy.accountingMethod === 'deferred_income'}
                    onChange={() => setNested('subsidy_config', 'accountingMethod', 'deferred_income')} />
                  <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Deferred Income Method</div>
                  <div className="text-[11px] text-slate-400 leading-relaxed">Held as a Deferred Government Grant liability, released to P&L as Other Income over the projection period — excluded from DSCR since it's non-cash.</div>
                </label>
                <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${subsidy.accountingMethod === 'asset_reduction' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
                  <input type="radio" className="hidden" checked={subsidy.accountingMethod === 'asset_reduction'}
                    onChange={() => setNested('subsidy_config', 'accountingMethod', 'asset_reduction')} />
                  <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Asset Reduction Method</div>
                  <div className="text-[11px] text-slate-400 leading-relaxed">Netted straight off the specific asset's gross block, lowering the depreciable base (both Companies Act and Income Tax Act basis). The most common real-world treatment when a subsidy is tied to a specific asset — bifurcate the amount per block in the Loan & Working Capital step.</div>
                </label>
              </div>
              {subsidy.accountingMethod === 'asset_reduction' && subsidyAmountEst > 0 && (
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3.5">
                  <Field label="Subsidy Status (for Balance Sheet reporting)" tooltip="Asset Reduction nets the subsidy off Building & Plant/Machinery's book value — auto-split between the two in the ratio of their own investment, no manual bifurcation needed. This toggle controls whether that reduction is actually applied: Granted shows the post-subsidy (reduced) asset values on the Depreciation step; Not Granted keeps the full asset values with zero reduction, for a case where sanction is still pending.">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setNested('subsidy_config', 'grantedForAssetReporting', true)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${subsidy.grantedForAssetReporting ? 'bg-[#ff5200] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>Granted</button>
                      <button type="button" onClick={() => setNested('subsidy_config', 'grantedForAssetReporting', false)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${!subsidy.grantedForAssetReporting ? 'bg-[#ff5200] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>Not Granted</button>
                    </div>
                  </Field>
                  {subsidy.grantedForAssetReporting && (
                    <>
                      <div className="mt-2.5 flex gap-3 flex-wrap">
                        <Badge tone="indigo">Building Reduction: ₹{fmt(subsidyForBuilding)}</Badge>
                        <Badge tone="indigo">Plant &amp; Machinery Reduction: ₹{fmt(subsidyForPM)}</Badge>
                      </div>
                      {Object.keys(subsidyTranchesForBadges).length > 0 && (
                        <div className="mt-2 flex gap-2 flex-wrap items-center">
                          <span className="text-[11px] text-slate-400">Applied by year (per disbursement date):</span>
                          {Object.keys(subsidyTranchesForBadges).sort((a, b) => a - b).map(y => (
                            <Badge key={y} tone="slate">{fyLabel(bp.baseFinancialYear, Number(y))}: ₹{fmt(subsidyTranchesForBadges[y])}</Badge>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="mt-6 mb-1 text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                Disbursement Timing
                <Badge tone={disbursedRemaining < 0 ? 'rose' : disbursedRemaining === 0 && subsidyAmountEst > 0 ? 'emerald' : 'slate'}>
                  ₹{fmt(subsidyDisbursedTotal)} of ₹{fmt(subsidyAmountEst)} allowed{disbursedRemaining < 0 ? ` — ₹${fmt(-disbursedRemaining)} over limit` : ''}
                </Badge>
              </div>
              <p className="text-xs text-slate-400 mb-2">It can happen in installments — add one row per disbursement. Each prepays the Term Loan the month it lands.</p>
              <ArrayEditor rows={subsidy.disbursements}
                columns={[{ key: 'date', label: 'Date of Disbursement', type: 'date' }, { key: 'amount', label: 'Amount of Disbursement', type: 'number' }]}
                onChange={rows => setNested('subsidy_config', 'disbursements', rows)} addLabel="+ Add Disbursement" />
            </>
          )}

          <div className="mt-5 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200">Include in base-case viability?</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Real bank DPRs default to <b>excluding</b> the subsidy from the primary DSCR/IRR numbers — it's contingent on government approval — and show it as an upside once received. Leave off unless the subsidy is already sanctioned.</div>
            </div>
            <button type="button" onClick={() => setNested('subsidy_config', 'includeInBaseCase', !subsidy.includeInBaseCase)}
              className={`flex-shrink-0 w-11 h-6 rounded-full relative transition-colors ${subsidy.includeInBaseCase ? 'bg-[#ff5200]' : 'bg-slate-300 dark:bg-slate-600'}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${subsidy.includeInBaseCase ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'raw_material' && (
        <div>
          <StepHeader title="Capacity & Raw Material" subtitle="Calculation of installed/annual capacity, then the sales & raw material consumption it drives" />
          <div className="grid grid-cols-2 gap-4 mb-5">
            <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${capacityTab === 'capacity' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
              <input type="radio" className="hidden" checked={capacityTab === 'capacity'} onChange={() => setCapacityTab('capacity')} />
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Production Capacity</div>
              <div className="text-[11px] text-slate-400 leading-relaxed">Installed capacity, shifts, working days, wastage and year-wise capacity utilization / operating months.</div>
            </label>
            <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${capacityTab === 'material' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
              <input type="radio" className="hidden" checked={capacityTab === 'material'} onChange={() => setCapacityTab('material')} />
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Material Costing</div>
              <div className="text-[11px] text-slate-400 leading-relaxed">Raw material recipe lines feeding sales & consumption — ingredient, quantity per output unit, cost and escalation.</div>
            </label>
          </div>

          {capacityTab === 'capacity' && (
            <div>
              <button type="button" onClick={() => setCalcCapacityOpen(o => !o)}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#ff5200] uppercase tracking-wide mb-2">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                  className={`transition-transform duration-200 ${calcCapacityOpen ? 'rotate-180' : ''}`}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
                </svg>
                Calculation of Capacity
              </button>
              {calcCapacityOpen && (
                <>
                  <div className="grid grid-cols-4 gap-x-5">
                    <Field label="Installed Output Capacity / Hour" tooltip="The FINAL PRODUCT's production rate (e.g. Oil, bottles, tablets) — not any raw material's. This matches how machinery is rated and how a bank reads a DPR's capacity figure. If the only figure you have is a raw-material intake rate, use 'Know it as Raw Material Input Capacity?' below to convert it.">
                      <NumberInput value={cap.installedCapacityPerHour} onChange={v => setNested('capacity_config', 'installedCapacityPerHour', v)} />
                    </Field>
                    <Field label="Unit" hint="Unit of the Final Product — MT, Ltr, Nos… whatever it's sold in"><ComboInput value={cap.capacityUnit} onChange={v => setNested('capacity_config', 'capacityUnit', v)} options={UOM_OPTIONS} placeholder="Type or pick a unit…" /></Field>
                    <Field label="Shift Hours" hint="Hours per shift, e.g. 8"><NumberInput value={cap.hoursPerDay} onChange={v => setNested('capacity_config', 'hoursPerDay', v)} /></Field>
                    <Field label="No. of Shifts" hint="e.g. 1, 2 or 3 shifts/day"><NumberInput value={cap.shiftsPerDay} onChange={v => setNested('capacity_config', 'shiftsPerDay', v)} /></Field>
                    <Field label="Working Days / Year"><NumberInput value={cap.workingDaysPerYear} onChange={v => setNested('capacity_config', 'workingDaysPerYear', v)} /></Field>
                    <Field label="Wastage %" tooltip="Finished-goods rejection/spoilage rate, since capacity is now output-based — not raw-material wastage."><NumberInput value={cap.wastagePct} onChange={v => setNested('capacity_config', 'wastagePct', v)} /></Field>
                  </div>

                  <button type="button" onClick={() => setOutputCalcOpen(o => !o)}
                    className="flex items-center gap-1.5 text-[11px] font-bold text-[#ff5200] hover:text-[#e04800] uppercase tracking-wide mb-3">
                    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                      className={`transition-transform duration-200 ${outputCalcOpen ? 'rotate-180' : ''}`}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
                    </svg>
                    Know it as Raw Material Input Capacity instead?
                  </button>
                  {outputCalcOpen && (() => {
                    const primaryLine = (rm.recipeLines || []).find(l => l.name);
                    const ratio = primaryLine?.qtyPerOutputUnit;
                    const hoursTotal = (cap.hoursPerDay || 0) * (cap.shiftsPerDay || 1);
                    const requiredOutputPerDay = (ratio > 0 && knownOutputPerDay > 0) ? knownOutputPerDay / ratio : null;
                    const requiredOutputPerHour = (requiredOutputPerDay !== null && hoursTotal > 0) ? requiredOutputPerDay / hoursTotal : null;
                    return (
                      <div className="rounded-xl border border-[#ff5200]/25 bg-orange-500/5 p-3.5 mb-5">
                        <div className="grid grid-cols-2 gap-x-5">
                          <Field label={`Known Raw Material Input Capacity / Day${primaryLine ? ` (${primaryLine.name}, ${primaryLine.unit || 'unit'})` : ''}`}>
                            <NumberInput value={knownOutputPerDay} onChange={setKnownOutputPerDay} placeholder="e.g. 300" />
                          </Field>
                          <div className="text-xs text-slate-500 dark:text-slate-400 flex flex-col justify-center">
                            {!primaryLine && <span>Add your primary <b>Raw Material</b> with its "Qty Required / 1 Unit of Output" on the Material Costing tab first — that's the conversion factor used here.</span>}
                            {primaryLine && !(ratio > 0) && <span>Set <b>{primaryLine.name}</b>'s "Qty Required / 1 Unit of Output" on the Material Costing tab — currently blank.</span>}
                            {primaryLine && ratio > 0 && <span>Using {primaryLine.name}'s ratio: <b>{ratio}</b> {primaryLine.unit || 'unit'} required per 1 {capUnit} of output.</span>}
                          </div>
                        </div>
                        {requiredOutputPerDay !== null && (
                          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#ff5200]/15">
                            <div>
                              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Equivalent Output Capacity / Day</div>
                              <div className="text-sm font-extrabold text-[#001254] dark:text-slate-100">{fmt(requiredOutputPerDay)} {capUnit}</div>
                            </div>
                            {requiredOutputPerHour !== null ? (
                              <>
                                <div>
                                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Equivalent Output Capacity / Hour</div>
                                  <div className="text-sm font-extrabold text-[#001254] dark:text-slate-100">{fmt(requiredOutputPerHour)} {capUnit}</div>
                                </div>
                                <button type="button" onClick={() => setNested('capacity_config', 'installedCapacityPerHour', Math.round(requiredOutputPerHour * 100) / 100)}
                                  className="ml-auto px-3 py-1.5 rounded-lg bg-[#ff5200] text-white text-xs font-bold hover:bg-[#e04800] transition-colors">
                                  Use This → Fill Output Capacity / Hour
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-slate-400">Set Shift Hours &amp; No. of Shifts above to get the per-hour figure.</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {capPerDay > 0 && (
                    <div className="grid grid-cols-3 gap-3 mb-5 mt-1">
                      <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3.5 py-2.5">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-0.5">Capacity / Day</div>
                        <div className="text-sm font-extrabold text-[#001254] dark:text-slate-100">{fmt(capPerDay)} <span className="text-[11px] font-semibold text-slate-400">{capUnit}</span></div>
                      </div>
                      <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3.5 py-2.5">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-0.5">Annual Output Capacity (Gross)</div>
                        <div className="text-sm font-extrabold text-[#001254] dark:text-slate-100">{fmt(capAnnualGross)} <span className="text-[11px] font-semibold text-slate-400">{capUnit}</span></div>
                      </div>
                      <div className="rounded-lg border border-[#ff5200]/30 bg-orange-500/5 px-3.5 py-2.5">
                        <div className="text-[10px] font-bold text-[#ff5200] uppercase tracking-wide mb-0.5">Annual Output Capacity (Net of Wastage)</div>
                        <div className="text-sm font-extrabold text-[#001254] dark:text-slate-100">{fmt(capAnnualNet)} <span className="text-[11px] font-semibold text-slate-400">{capUnit}</span></div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <button type="button" onClick={() => setAnnualCapacityOpen(o => !o)}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#ff5200] uppercase tracking-wide mb-2 mt-2">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                  className={`transition-transform duration-200 ${annualCapacityOpen ? 'rotate-180' : ''}`}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
                </svg>
                Annual Capacity
              </button>
              {annualCapacityOpen && (
                <>
                  <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-900">
                          <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Year</th>
                          <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Capacity Utilization %</th>
                          <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Annual Output Production</th>
                          <th className="border-b border-slate-200 dark:border-slate-700"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rampUpYears.map((pct, i) => (
                          <tr key={i} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/30">
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 font-semibold text-slate-600 dark:text-slate-300">{fyLabel(bp.baseFinancialYear, i + 1)}</td>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800"><NumberInput value={pct} onChange={v => setCapacityYearPct(i, v)} /></td>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">
                              {fmt(capAnnualNet * (pct || 0) / 100 * (yearMonths(i) / normalOperatingMonths))} <span className="text-[11px] text-slate-400">{capUnit}</span>
                            </td>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                              {rampUpYears.length > 1 && <button type="button" onClick={() => removeCapacityYear(i)} className="text-rose-500 text-xs hover:underline whitespace-nowrap">Remove</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <AddButton onClick={addCapacityYear} className="mt-2.5">Add Year</AddButton>
                </>
              )}

              <button type="button" onClick={() => setOperatingMonthsOpen(o => !o)}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#ff5200] uppercase tracking-wide mb-2 mt-6">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                  className={`transition-transform duration-200 ${operatingMonthsOpen ? 'rotate-180' : ''}`}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
                </svg>
                Vary Operating Months by Year?
              </button>
              {operatingMonthsOpen && (
                <>
                  <div className="max-w-xs mb-3">
                    <Field label="Normal Operating Months per Year" tooltip="default 12 — most industries run all year. A seasonal business (e.g. rice bran/paddy extraction, sugar milling) whose raw material is only available part of the year should set its actual season length here (e.g. 8) — every ratio below scales against this, not a fixed 12.">
                      <NumberInput value={cap.normalOperatingMonths} onChange={v => setNested('capacity_config', 'normalOperatingMonths', v)} placeholder="12" />
                    </Field>
                  </div>
                  <Field tooltip="Year 1 is often a partial year — commissioning happens mid-year, so the plant doesn't run its full normal season. Turn this on to override specific years; every year not overridden defaults to the Normal Operating Months above.">
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setNested('capacity_config', 'useOperatingMonths', true)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${cap.useOperatingMonths ? 'bg-[#ff5200] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>Yes</button>
                      <button type="button" onClick={() => set('capacity_config', { ...cap, useOperatingMonths: false, operatingMonthsByYear: [] })}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${!cap.useOperatingMonths ? 'bg-[#ff5200] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>No</button>
                    </div>
                  </Field>

                  {cap.useOperatingMonths && (
                    <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700 max-w-md mt-3">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-50 dark:bg-slate-900">
                            <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Year</th>
                            <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Operating Months</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rampUpYears.map((_, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 font-semibold text-slate-600 dark:text-slate-300">{fyLabel(bp.baseFinancialYear, i + 1)}</td>
                              <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                                <NumberInput value={(cap.operatingMonthsByYear || [])[i]} onChange={v => setOperatingMonth(i, v)} placeholder={String(cap.normalOperatingMonths || 12)} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {capacityTab === 'material' && (
            <div>
              <div className="mb-1 text-xs font-bold text-slate-500 uppercase tracking-wide">Raw Material Inputs</div>
              <div className="mb-2.5 text-[11px] text-slate-400 leading-relaxed">
                How much of each raw material is required to produce <b>1 {capUnit}</b> of {cap.productName || 'the Final Product'} — e.g. 6.25 MT Rice Bran per 1 MT Oil. The first row is treated as the primary raw material (used by the reverse-calculator above and by-product auto-remainder below); add further rows for any other input (e.g. packaging, a secondary consumable).
              </div>
              <ArrayEditor
                rows={rm.recipeLines}
                columns={[
                  { key: 'name', label: 'Ingredient' },
                  { key: 'unit', label: 'Unit', type: 'combo', options: UOM_OPTIONS, placeholder: 'Unit…' },
                  { key: 'qtyPerOutputUnit', label: `Qty Required / 1 ${capUnit}`, type: 'number' },
                  { key: 'unitCost', label: 'Cost / Input Unit', type: 'number' },
                  { key: 'escalationPct', label: 'Escalation % / yr', type: 'number', noTotal: true },
                  { key: 'closingStockDays', label: 'Closing Stock (Days)', type: 'number' },
                ]}
                onChange={rows => set('raw_material', { ...rm, recipeLines: rows })}
                addLabel="+ Add Raw Material"
                extraActions={(rm.recipeLines || []).some(l => l.name) && (
                  <button type="button" onClick={() => setRmProcurementOpen(o => !o)}
                    style={{ background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)' }}
                    className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow-sm hover:shadow-md transition-shadow">
                    Raw Material Procurement {rmProcurementOpen ? '▲' : '▼'}
                  </button>
                )}
              />

              {rmProcurementOpen && (() => {
                // Use the Annual Capacity table's row count directly rather
                // than caseData.projection_years — that field syncs via the
                // effect above, but autosave is debounced, so relying on it
                // here could show a stale year count for a moment (or longer,
                // if the sync hasn't landed yet). This can never lag.
                const preview = computeRmProcurementPreview(capacityYearCount > 0 ? { ...caseData, projection_years: capacityYearCount } : caseData);
                const lineNames = (rm.recipeLines || []).filter(l => l.name).map(l => l.name);
                if (lineNames.length === 0) return null;
                const procurementUnitDivisor = { actual: 1, lakh: 100000, crore: 10000000 }[procurementCostUnit];
                const procurementUnitLabel = { actual: '', lakh: ' (₹ Lakhs)', crore: ' (₹ Crore)' }[procurementCostUnit];
                return (
                  <div className="mt-3">
                    <div className="flex justify-end mb-1.5">
                      <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                        {[{ v: 'actual', l: 'Actual Value' }, { v: 'lakh', l: 'Lakhs' }, { v: 'crore', l: 'Crore' }].map(o => (
                          <button key={o.v} type="button" onClick={() => setProcurementCostUnit(o.v)}
                            className={`px-3 py-1.5 text-[11px] font-bold transition-colors ${procurementCostUnit === o.v ? 'bg-[#ff5200] text-white' : 'bg-white dark:bg-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-900">
                          <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Particulars</th>
                          {preview.map((y, i) => <th key={i} className="text-right px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{fyLabel(bp.baseFinancialYear, y.year)}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: 'Capacity Utilization %', get: y => y.utilPct },
                          { label: 'Operating Months', get: y => y.monthsThisYear },
                          { label: `Annual Output Produced (${capUnit})`, get: y => y.productionQty, bold: true },
                        ].map((row, i) => (
                          <tr key={i} className={row.bold ? 'font-bold bg-orange-500/5' : ''}>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{row.label}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(row.get(y))}</td>)}
                          </tr>
                        ))}
                        {lineNames.map((name, li) => (
                          <React.Fragment key={li}>
                            <tr><td colSpan={preview.length + 1} className="px-3 py-1.5 text-[10px] font-extrabold text-slate-500 uppercase tracking-wide bg-slate-50/80 dark:bg-slate-900/60">Raw Material Purchase Quantity — {name}</td></tr>
                            {[
                              { label: 'Used in Production', get: y => y.lines[li]?.qty },
                              { label: 'Add: Closing Stock', get: y => y.lines[li]?.closingStockQty },
                              { label: 'Less: Opening Stock', get: y => y.lines[li]?.openingStockQty },
                              { label: 'Raw Material Purchased', get: y => y.lines[li]?.purchasedQty, bold: true },
                              { label: 'Purchase Price / Unit', get: y => y.lines[li]?.unitCost },
                              { label: `Annual Procurement Cost${procurementUnitLabel}`, get: y => (y.lines[li]?.procurementCost || 0) / procurementUnitDivisor, bold: true },
                            ].map((row, i) => (
                              <tr key={i} className={row.bold ? 'font-bold bg-orange-500/5' : ''}>
                                <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{row.label}</td>
                                {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(row.get(y))}</td>)}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                );
              })()}

              <div className="mb-1 text-xs font-bold text-slate-500 uppercase tracking-wide mt-6">Products &amp; By-Products</div>
              <div className="mb-2.5 text-[11px] text-slate-400 leading-relaxed">
                Mark exactly one row <b>Main Product</b> — what the Capacity tab's Output Capacity is rated on. Map each row to the Raw Material(s) it's made from (a bottle might map to PET Granule <i>and</i> Cap; Rice Bran Oil maps to Rice Bran alone). <b>Yield / 1 Unit of Raw Material</b> is how much of this output comes from 1 unit of its mapped raw material — e.g. 0.16 MT Oil per 1 MT Rice Bran. Leave a By-Product's Yield blank to auto-fill it as the remainder against other rows mapped to the same raw material (e.g. De-Oiled Cake auto-fills to 0.84 once Oil's Yield is set to 0.16).
              </div>
              <ArrayEditor
                rows={rm.outputs}
                columns={[
                  { key: 'name', label: 'Product', width: '15%' },
                  { key: 'kind', label: 'Type', type: 'select', width: '12%', options: [{ value: 'main', label: 'Main Product' }, { value: 'by_product', label: 'By-Product' }] },
                  { key: 'ingredients', label: 'Ingredient', type: 'multiselect', width: '17%', options: rawMaterialNames },
                  { key: 'unit', label: 'Unit', type: 'combo', width: '9%', options: UOM_OPTIONS, placeholder: 'Unit…' },
                  { key: 'yieldPerRmUnit', label: 'Yield / 1 Unit of RM', type: 'number', width: '13%' },
                  { key: 'pricePerUnit', label: 'Selling Price / Unit', type: 'number', width: '12%' },
                  { key: 'escalationPct', label: 'Escalation % / yr', type: 'number', width: '11%', noTotal: true },
                  { key: 'closingStockDays', label: 'Closing Stock (Days)', type: 'number', width: '11%' },
                ]}
                onChange={rows => set('raw_material', { ...rm, outputs: rows })}
                addLabel="+ Add Product / By-Product"
                extraActions={(rm.outputs || []).some(o => o.name) && (
                  <button type="button" onClick={() => setSalesEstimateOpen(o => !o)}
                    style={{ background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)' }}
                    className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow-sm hover:shadow-md transition-shadow">
                    Sales Estimation {salesEstimateOpen ? '▲' : '▼'}
                  </button>
                )}
              />

              {(() => {
                const outputRows = (rm.outputs || []).filter(o => o.name);
                if (outputRows.length === 0 || capAnnualNet <= 0) return null;
                const rmLines = rm.recipeLines || [];
                const yieldEntered = (o) => o.yieldPerRmUnit !== undefined && o.yieldPerRmUnit !== null && o.yieldPerRmUnit !== '';
                const mainRow = outputRows.find(o => o.kind === 'main');
                const mainRmLine = mainRow ? rmLines.find(l => l.name === (mainRow.ingredients || [])[0]) : null;
                const mismatch = mainRow && mainRmLine && yieldEntered(mainRow) && mainRmLine.qtyPerOutputUnit > 0
                  && Math.abs((1 / mainRow.yieldPerRmUnit) - mainRmLine.qtyPerOutputUnit) > 0.01 * mainRmLine.qtyPerOutputUnit;
                return (
                  <>
                    {mismatch && (
                      <div className="mt-3 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
                        Main Product's Yield ({mainRow.yieldPerRmUnit}) implies {fmt(1 / mainRow.yieldPerRmUnit)} {mainRmLine.unit} of {mainRmLine.name} per 1 {capUnit} — but the Raw Material Inputs table has "Qty Required" set to {mainRmLine.qtyPerOutputUnit}. These two figures should be reciprocals of each other; check which one is correct.
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {outputRows.map((o, i) => {
                        const rmName = (o.ingredients || [])[0];
                        const rmLine = rmLines.find(l => l.name === rmName);
                        const isMain = o.kind === 'main';
                        let qty, note;
                        if (isMain) {
                          qty = capAnnualNet;
                        } else {
                          const rmAnnualQty = rmLine ? capAnnualNet * (rmLine.qtyPerOutputUnit || 0) : 0;
                          let effectiveYield = o.yieldPerRmUnit;
                          if (!yieldEntered(o)) {
                            const otherYields = outputRows.filter(x => x !== o && (x.ingredients || [])[0] === rmName && yieldEntered(x))
                              .reduce((s, x) => s + Number(x.yieldPerRmUnit || 0), 0);
                            effectiveYield = Math.max(1 - otherYields, 0);
                            note = `Auto: ${fmt(effectiveYield)}`;
                          }
                          qty = rmAnnualQty * (effectiveYield || 0);
                        }
                        return (
                          <div key={i} className={`rounded-lg border px-3.5 py-2.5 ${isMain ? 'border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                            <div className={`text-[10px] font-bold uppercase tracking-wide mb-0.5 ${isMain ? 'text-emerald-600' : 'text-slate-400'}`}>
                              Annual Qty — {o.name} {note ? `(${note})` : ''}
                            </div>
                            <div className="text-sm font-extrabold text-[#001254] dark:text-slate-100">
                              {(!isMain && !rmName) ? '—' : fmt(qty)} <span className="text-[11px] font-semibold text-slate-400">{o.unit || 'unit'}</span>
                            </div>
                            {!isMain && !rmName && <div className="text-[10px] text-amber-600 mt-0.5">Map an Ingredient to compute this</div>}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

              {salesEstimateOpen && (() => {
                const preview = computeSalesEstimatePreview(capacityYearCount > 0 ? { ...caseData, projection_years: capacityYearCount } : caseData);
                if (preview.length === 0 || !preview[0].products.some(p => p.name)) return null;
                const productNames = preview[0].products.map(p => ({ name: p.name, kind: p.kind }));
                const salesUnitDivisor = { actual: 1, lakh: 100000, crore: 10000000 }[salesValueUnit];
                const salesUnitLabel = { actual: '', lakh: ' (₹ Lakhs)', crore: ' (₹ Crore)' }[salesValueUnit];
                const sumByKind = (y, kind) => y.products.filter(p => p.kind === kind).reduce((s, p) => s + (p.salesValue || 0), 0);
                return (
                  <div className="mt-4">
                    <div className="flex justify-end mb-1.5">
                      <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                        {[{ v: 'actual', l: 'Actual Value' }, { v: 'lakh', l: 'Lakhs' }, { v: 'crore', l: 'Crore' }].map(o => (
                          <button key={o.v} type="button" onClick={() => setSalesValueUnit(o.v)}
                            className={`px-3 py-1.5 text-[11px] font-bold transition-colors ${salesValueUnit === o.v ? 'bg-[#ff5200] text-white' : 'bg-white dark:bg-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-900'}`}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-900">
                          <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Particulars</th>
                          {preview.map((y, i) => <th key={i} className="text-right px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{fyLabel(bp.baseFinancialYear, y.year)}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: 'Annual Capacity Utilization (%)', get: y => y.utilPct },
                          { label: 'Operating Months', get: y => y.monthsThisYear },
                        ].map((row, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{row.label}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(row.get(y))}</td>)}
                          </tr>
                        ))}

                        <tr><td colSpan={preview.length + 1} className="px-3 py-1.5 text-[10px] font-extrabold text-slate-500 uppercase tracking-wide bg-slate-50/80 dark:bg-slate-900/60">Total Production (Quantity)</td></tr>
                        {productNames.map((p, pi) => (
                          <tr key={pi}>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{p.name}{p.kind === 'by_product' ? ' (By-Product)' : ' (Main Product)'}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(y.products[pi]?.productionQty)}</td>)}
                          </tr>
                        ))}

                        <tr><td colSpan={preview.length + 1} className="px-3 py-1.5 text-[10px] font-extrabold text-slate-500 uppercase tracking-wide bg-slate-50/80 dark:bg-slate-900/60">Finished Goods — Closing Stock (Quantity)</td></tr>
                        {productNames.map((p, pi) => (
                          <tr key={pi}>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Closing Stock — {p.name}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(y.products[pi]?.closingStockQty)}</td>)}
                          </tr>
                        ))}

                        <tr><td colSpan={preview.length + 1} className="px-3 py-1.5 text-[10px] font-extrabold text-slate-500 uppercase tracking-wide bg-slate-50/80 dark:bg-slate-900/60">Sales (Quantity Available for Sale)</td></tr>
                        {productNames.map((p, pi) => (
                          <tr key={pi}>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Sales — {p.name}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(y.products[pi]?.netSalesQty)}</td>)}
                          </tr>
                        ))}

                        {productNames.map((p, pi) => (
                          <tr key={pi}>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Sale Price — {p.name}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(y.products[pi]?.pricePerUnit)}</td>)}
                          </tr>
                        ))}
                        {productNames.some(p => p.kind === 'main') && (
                          <tr className="font-bold">
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Sales Value of Main Product{salesUnitLabel}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(sumByKind(y, 'main') / salesUnitDivisor)}</td>)}
                          </tr>
                        )}
                        {productNames.some(p => p.kind === 'by_product') && (
                          <tr className="font-bold">
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Sales Value of By-Products{salesUnitLabel}</td>
                            {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(sumByKind(y, 'by_product') / salesUnitDivisor)}</td>)}
                          </tr>
                        )}
                        <tr className="font-bold bg-orange-500/5">
                          <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Grand Total Sales Value{salesUnitLabel}</td>
                          {preview.map((y, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(y.totalSalesValue / salesUnitDivisor)}</td>)}
                        </tr>
                      </tbody>
                    </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'man_power' && (
        <div>
          <StepHeader title="Man Power" />
          <div className="grid grid-cols-4 gap-x-5 mb-2">
            <Field label="Wage Escalation % / yr"><NumberInput value={labour.escalationPct} onChange={v => setNested('labour_config', 'escalationPct', v)} /></Field>
            <Field label="Variable % (for Break-Even split)" hint="default 60" tooltip="Labour — a factory needs a permanent skeleton crew (supervisors, security, maintenance) regardless of whether it's running at 20% or 100% capacity — that's the Fixed portion. On top of that, shift workers, overtime and casual labour scale with how much is actually being produced — that's the Variable portion. Hence 60% Variable / 40% Fixed by default — a more even split, since a factory's core labour cost is meaningfully sticky in a way power isn't. This split also drives how Factory Wages actually move year to year in the P&L (the Variable share scales with that year's capacity utilization), not just how the Break-Even Analysis classifies the cost.">
              <NumberInput value={labour.variablePct} onChange={v => setNested('labour_config', 'variablePct', v)} />
            </Field>
          </div>
          <button type="button" onClick={() => setFactoryWorkersOpen(o => !o)}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#ff5200] uppercase tracking-wide mb-2">
            <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
              className={`transition-transform duration-200 ${factoryWorkersOpen ? 'rotate-180' : ''}`}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
            </svg>
            Factory Workers
          </button>
          {factoryWorkersOpen && (
            <ArrayEditor rows={labour.factory} nameCatalog={COMPONENT_CATALOG.man_power}
              columns={[
                { key: 'role', label: 'Role' }, { key: 'count', label: 'Count', type: 'number' }, { key: 'monthlyWage', label: 'Salary / Month', type: 'number' },
                { key: 'perAnnum', label: 'Per Annum', type: 'computed', compute: r => (r.monthlyWage || 0) * 12 * (r.count || 0) },
              ]}
              onChange={rows => set('labour_config', { ...labour, factory: rows })} />
          )}

          <button type="button" onClick={() => setAdminStaffOpen(o => !o)}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#ff5200] uppercase tracking-wide mb-2 mt-4">
            <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
              className={`transition-transform duration-200 ${adminStaffOpen ? 'rotate-180' : ''}`}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
            </svg>
            Admin / Office Staff
          </button>
          {adminStaffOpen && (
            <ArrayEditor rows={labour.admin} nameCatalog={COMPONENT_CATALOG.man_power}
              columns={[
                { key: 'role', label: 'Role' }, { key: 'count', label: 'Count', type: 'number' }, { key: 'monthlyWage', label: 'Salary / Month', type: 'number' },
                { key: 'perAnnum', label: 'Per Annum', type: 'computed', compute: r => (r.monthlyWage || 0) * 12 * (r.count || 0) },
              ]}
              onChange={rows => set('labour_config', { ...labour, admin: rows })} />
          )}
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'direct_expenses' && (
        <div>
          <StepHeader title="Direct Expenses" subtitle="Factory-level overheads that are part of Cost of Production, deducted above Gross Profit — see Indirect Expenses for Admin/Selling costs, deducted below it" />
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Packaging</div>
          <div className="grid grid-cols-3 gap-x-5 mb-5">
            <Field label="Packaging Cost / Unit" tooltip="Costed against the Main Product's net sales qty each year — auto-scales with volume, unlike a flat Custom Direct Expense below.">
              <NumberInput value={sales.packagingCostPerUnit} onChange={v => setNested('sales_config', 'packagingCostPerUnit', v)} />
            </Field>
            <Field label="Packaging Escalation % / yr"><NumberInput value={sales.packagingEscalationPct} onChange={v => setNested('sales_config', 'packagingEscalationPct', v)} /></Field>
          </div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Custom Direct Expenses</div>
          <p className="text-xs text-slate-400 mb-2">Power & Fuel, Insurance, Repairs & Maintenance, or anything else — work out the annual amount yourself and add it here, escalated per year, added straight into Cost of Production. Type a name into a row's <b>Group</b> column to group expenses your own way (e.g. "Direct Manufacturing Cost", "Packaging Cost") — leave it blank and everything stays in one plain list.</p>
          <GroupedArrayEditor rows={power.customExpenses} nameCatalog={COMPONENT_CATALOG.direct_expenses}
            columns={[{ key: 'name', label: 'Expense' }, { key: 'amount', label: 'Annual Amount', type: 'number' }, { key: 'escalationPct', label: 'Escalation % / yr', type: 'number', noTotal: true }]}
            onChange={rows => set('power_config', { ...power, customExpenses: rows })} addLabel="+ Add Direct Expense" />
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'indirect_expenses' && (
        <div>
          <StepHeader title="Indirect Expenses" subtitle="Administrative & Selling overheads — sit below Gross Profit, not part of Cost of Production" />
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Custom Indirect Expenses</div>
          <p className="text-xs text-slate-400 mb-2">Administrative Salary, Director Remuneration, Professional Fees, Office Rent, etc. — annual amount, escalated per year, deducted after Gross Profit.</p>
          <ArrayEditor rows={indirect.customLines} nameCatalog={COMPONENT_CATALOG.indirect_expenses}
            columns={[{ key: 'name', label: 'Expense' }, { key: 'amount', label: 'Annual Amount', type: 'number' }, { key: 'escalationPct', label: 'Escalation % / yr', type: 'number', noTotal: true }]}
            onChange={rows => set('indirect_expenses', { ...indirect, customLines: rows })} addLabel="+ Add Indirect Expense" />
          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'sources_of_finance' && (
        <div>
          <StepHeader title="Promoter's Contribution" subtitle="Everything funding this project from the promoter's own end — equity, reserves, unsecured loans from directors — besides the Term Loan and Subsidy (set in their own steps)" total={equitySourcesTotal} />
          <FundingBadge caseData={caseData} />

          <Card className="p-4 mb-5 bg-slate-50/60 dark:bg-slate-900/40">
            <div className="text-[11px] font-extrabold text-[#001254] dark:text-slate-200 uppercase tracking-wide mb-2">How Bank Finance Is Decided</div>
            <p className="text-xs text-slate-400 mb-3 leading-relaxed">
              A bank sanctions its Term Loan against <b>Block Capital</b> (Land / Building / Plant &amp; Machinery / Other Assets) — not against IDCP, Pre-Operative Expenses or Working Capital Margin, which are funded entirely out of the Promoter's own Margin. Decide your <b>Promoter Margin %</b> of Block Capital here; the rest becomes the <b>Bank Finance target</b> — which then gets allocated across each category on its own step (Bank Finance % there defaults to this figure until you override it per category) and should match what the Term Loan step ends up sanctioning. Everything outside Block Capital automatically falls into the Promoter's Margin below.
            </p>
            <div className="grid grid-cols-3 gap-x-5">
              <Field label="Block Capital (Land+Building+P&M+Other Assets)"><div className="text-sm font-bold text-slate-700 dark:text-slate-200 py-2.5">₹{fmt(blockCapitalTotal)}</div></Field>
              <Field label="Promoter Margin % of Block Capital"><NumberInput value={mof.promoterMarginPct} onChange={v => setNested('means_of_finance', 'promoterMarginPct', v)} placeholder="e.g. 25" /></Field>
              <Field label="Bank Finance % (auto)"><div className="text-sm font-bold text-slate-700 dark:text-slate-200 py-2.5">{overallBankFinancePct === undefined ? '—' : `${overallBankFinancePct}%`}</div></Field>
            </div>
            {overallBankFinancePct !== undefined && (
              <div className="flex gap-3 flex-wrap mt-1">
                <Badge tone="indigo">Bank Finance Target: ₹{fmt(bankFinanceTargetAmount)}</Badge>
                <Badge tone="indigo">Promoter Contribution Target: ₹{fmt(promoterMarginTargetAmount)} (Block Capital margin + all of IDCP/Pre-Op/WC Margin)</Badge>
                <Badge tone={Math.abs(equitySourcesTotal - promoterMarginTargetAmount) < 1 ? 'emerald' : 'amber'}>
                  Entered Promoter Contribution: ₹{fmt(equitySourcesTotal)}{Math.abs(equitySourcesTotal - promoterMarginTargetAmount) >= 1 ? ` — ₹${fmt(Math.abs(equitySourcesTotal - promoterMarginTargetAmount))} ${equitySourcesTotal > promoterMarginTargetAmount ? 'over' : 'short of'} target` : ' — matches target'}
                </Badge>
              </div>
            )}
          </Card>

          <ArrayEditor rows={mof.equitySources} nameCatalog={COMPONENT_CATALOG.sources_of_finance}
            columns={[{ key: 'name', label: 'Source' }, { key: 'amount', label: 'Amount', type: 'number' }]}
            onChange={rows => set('means_of_finance', { ...mof, equitySources: rows })} addLabel="+ Add Source" />

          {(mof.wcLimit > 0) && (
            <Card className="p-4 mt-5 bg-slate-50/60 dark:bg-slate-900/40">
              <div className="text-[11px] font-extrabold text-[#001254] dark:text-slate-200 uppercase tracking-wide mb-2">Full Funding Picture (informational)</div>
              <p className="text-xs text-slate-400 mb-3 leading-relaxed">
                Cost of Project above only carries the Working Capital <b>Margin</b> (the promoter's own seed cash) — the Working Capital facility itself (Cash Credit / Overdraft, set on the Working Capital step) is sanctioned separately from the Term Loan and doesn't flow through the Cost-of-Project reconciliation. Purely for reference, here's the complete funding stack including that facility:
              </p>
              <div className="grid grid-cols-2 gap-x-5 gap-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                <div>Promoter Contribution <span className="float-right font-bold">₹{fmt(equitySourcesTotal)}</span></div>
                <div>Term Loan (Block Capital) <span className="float-right font-bold">₹{fmt(tl.amount || 0)}</span></div>
                <div>Working Capital Facility (CC/OD) <span className="float-right font-bold">₹{fmt(mof.wcLimit || 0)}</span></div>
                {subsidyAmountEst > 0 && (
                  <div>
                    Subsidy {subsidy.timing === 'upfront' ? '' : '(Backended — not counted below)'}
                    <span className="float-right font-bold">{subsidy.timing === 'upfront' ? `₹${fmt(subsidyAmountEst)}` : `(₹${fmt(subsidyAmountEst)})`}</span>
                  </div>
                )}
              </div>
              {subsidyAmountEst > 0 && subsidy.timing !== 'upfront' && (
                <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">
                  This subsidy is backended — disbursed only after the project is operational, so it funds nothing on Day 1. It prepays/reduces the Term Loan when it actually arrives, shown here for disclosure only (0% weight in the total below) — same treatment as a real bank-submitted Means of Finance table.
                </p>
              )}
              <div className="mt-2.5 pt-2.5 border-t border-slate-200 dark:border-slate-700">
                <Badge tone="indigo">Total Funding incl. WC Facility: ₹{fmt(equitySourcesTotal + (tl.amount || 0) + (mof.wcLimit || 0) + (subsidy.timing === 'upfront' ? (subsidyAmountEst || 0) : 0))}</Badge>
              </div>
            </Card>
          )}

          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'term_loan' && (
        <div>
          <StepHeader title="Term Loan" subtitle="Disbursement and repayment schedule" />
          <FundingBadge caseData={caseData} />
          {bankFinanceTargetAmount > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge tone={Math.abs((tl.amount || 0) - bankFinanceTargetAmount) < 1 ? 'emerald' : 'amber'}
                  title="Sanctioned against Block Capital (Land/Building/Plant & Machinery/Other Assets) only — IDCP, Pre-Operative Expenses and Working Capital Margin are funded entirely out of the Promoter's Margin, not the loan, so this target doesn't move as IDCP changes.">
                  Bank Finance Target (Block Capital × Bank Finance %): ₹{fmt(bankFinanceTargetAmount)} — Loan Amount entered: ₹{fmt(tl.amount || 0)}
                  {Math.abs((tl.amount || 0) - bankFinanceTargetAmount) >= 1
                    ? ` — ₹${fmt(Math.abs((tl.amount || 0) - bankFinanceTargetAmount))} ${(tl.amount || 0) > bankFinanceTargetAmount ? 'over' : 'short of'} target`
                    : ' — matches target'}
                </Badge>
                {tl.disbursementType !== 'installment_wise' && Math.abs((tl.amount || 0) - bankFinanceTargetAmount) >= 1 && (
                  <button type="button" onClick={() => setNested('term_loan_config', 'amount', bankFinanceTargetAmount)}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[11px] font-bold text-[#001254] dark:text-slate-200 hover:border-[#ff5200]/40 hover:text-[#ff5200] transition-colors">
                    Use Target: ₹{fmt(bankFinanceTargetAmount)}
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${(tl.disbursementType || 'lump_sum') === 'lump_sum' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
              <input type="radio" className="hidden" checked={(tl.disbursementType || 'lump_sum') === 'lump_sum'}
                onChange={() => setNested('term_loan_config', 'disbursementType', 'lump_sum')} />
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Lump Sum Disbursement</div>
              <div className="text-[11px] text-slate-400 leading-relaxed">The full sanctioned amount is drawn in one go — the common case for smaller, single-tranche loans.</div>
            </label>
            <label className={`p-3.5 rounded-xl border cursor-pointer transition-colors ${tl.disbursementType === 'installment_wise' ? 'border-[#ff5200] bg-orange-500/5' : 'border-slate-200 dark:border-slate-700'}`}>
              <input type="radio" className="hidden" checked={tl.disbursementType === 'installment_wise'}
                onChange={() => setNested('term_loan_config', 'disbursementType', 'installment_wise')} />
              <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Installment-wise Disbursement</div>
              <div className="text-[11px] text-slate-400 leading-relaxed">Drawn in tranches as construction progresses — interest accrues only on what's actually been released each month, not the full sanctioned amount from day one.</div>
            </label>
          </div>

          <div className="grid grid-cols-4 gap-x-5">
            {tl.disbursementType !== 'installment_wise' && (
              <Field label="Loan Amount"><NumberInput value={tl.amount} onChange={v => setNested('term_loan_config', 'amount', v)} /></Field>
            )}
            <Field label="Interest Rate % p.a."><NumberInput value={tl.ratePct} onChange={v => setNested('term_loan_config', 'ratePct', v)} /></Field>
            <Field label="Total Tenor (months, door-to-door)"><NumberInput value={tl.tenorMonths} onChange={v => setNested('term_loan_config', 'tenorMonths', v)} /></Field>
            <Field label="Moratorium (months)"><NumberInput value={tl.moratoriumMonths} onChange={v => setNested('term_loan_config', 'moratoriumMonths', v)} /></Field>
            {tl.disbursementType === 'installment_wise' && (
              <Field label="First Disbursement Date" tooltip="The reference date each tranche's month is measured from — normally your first drawdown.">
                <input type="date" value={tl.disbursementDate || ''} onChange={e => setNested('term_loan_config', 'disbursementDate', e.target.value)}
                  className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:border-[#ff5200]" />
              </Field>
            )}
          </div>

          {tl.disbursementType === 'installment_wise' && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <button type="button" onClick={() => setTranchesOpen(o => !o)}
                  className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#ff5200] uppercase tracking-wide">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                    className={`transition-transform duration-200 ${tranchesOpen ? 'rotate-180' : ''}`}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
                  </svg>
                  Disbursement Tranches
                </button>
                <Badge tone="indigo">Total Loan Amount: ₹{fmt(tranchesTotal)}</Badge>
              </div>
              {tranchesOpen && (
                <ArrayEditor rows={tl.disbursementTranches}
                  columns={[{ key: 'date', label: 'Date', type: 'date' }, { key: 'amount', label: 'Amount', type: 'number' }]}
                  onChange={rows => setNested('term_loan_config', 'disbursementTranches', rows)} addLabel="+ Add Tranche" />
              )}
            </div>
          )}

          {tl.amount > 0 && tl.tenorMonths > 0 && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <button type="button" onClick={() => setPreviewOpen(o => !o)}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 hover:text-[#ff5200] uppercase tracking-wide">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                    className={`transition-transform duration-200 ${previewOpen ? 'rotate-180' : ''}`}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
                  </svg>
                  Live Repayment Preview — updates as you edit the fields above
                </button>
                <RepaymentScheduleTool tl={effectiveTl} projectionYears={capacityYearCount || caseData.projection_years || 6} applicantName={caseData.applicant_name}
                  subsidyPrepayment={subsidyPrepayments}
                  onSetDisbursementDate={d => setNested('term_loan_config', 'disbursementDate', d)} />
              </div>
              {previewOpen && (
                <>
                  <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-900">
                          {['Year', 'Opening Balance', 'Interest', 'Installment', 'Closing Balance'].map(h => (
                            <th key={h} className="text-right first:text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRepaymentByYear(effectiveTl, Math.ceil((tl.tenorMonths || 60) / 12), subsidyPrepayments).map(row => (
                          <tr key={row.year}>
                            <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">Year {row.year}</td>
                            <td className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">₹{fmt(row.openingBalance)}</td>
                            <td className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">₹{fmt(row.interest)}</td>
                            <td className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">₹{fmt(row.installment)}</td>
                            <td className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 font-semibold">₹{fmt(row.closingBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {tl.moratoriumMonths > 0 && (
                    <p className="text-[11px] text-slate-400 mt-1.5">Moratorium of {tl.moratoriumMonths} months means no installment is due in that window — interest still accrues and is either serviced or capitalised as IDCP (see Preoperative Expenses step).</p>
                  )}
                </>
              )}
            </div>
          )}

          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'depreciation' && (
        <div>
          <StepHeader title="Depreciation" subtitle="Fixed asset blocks — drives P&L depreciation, Net Block on the Balance Sheet, and tax vs. book depreciation" />
          <p className="text-xs text-slate-400 mb-2">
            Land, Building, Plant &amp; Machinery and Other Assets each get their own block automatically — Investment Amount is the total you've already entered for that head on the Block Capital steps, IDCP and any Asset-Reduction subsidy split between Building and Plant &amp; Machinery in the ratio of their investment. Land is fetched too but locked at 0% — non-depreciable. Just fill in each depreciable block's Rate % and Method (SLM/WDV) — nothing to re-type. Add further blocks yourself for anything not tracked there (Furniture &amp; Fixtures, Vehicles, etc.).
          </p>
          {blockCapitalTotal > 0 && (
            <div className="mb-3">
              <Badge tone={Math.abs(depreciationBlocksTotal - blockCapitalTotal) < 1 ? 'emerald' : 'amber'}>
                {Math.abs(depreciationBlocksTotal - blockCapitalTotal) < 1
                  ? `✓ Depreciation Blocks (₹${fmt(depreciationBlocksTotal)}) match Block Capital Total (₹${fmt(blockCapitalTotal)})`
                  : `Depreciation Blocks total ₹${fmt(depreciationBlocksTotal)} vs Block Capital ₹${fmt(blockCapitalTotal)} — ₹${fmt(Math.abs(depreciationBlocksTotal - blockCapitalTotal))} difference, likely a manually-added block not part of Block Capital`}
              </Badge>
            </div>
          )}
          <DepreciationBlockEditor blocks={fa.blocks} onChange={rows => set('fixed_assets', { ...fa, blocks: rows })}
            extraActions={
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setCaseData(c => syncDepreciationBlocks(c))} title="Re-pull the latest totals from the Block Capital steps"
                  className="px-3.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-bold text-[#001254] dark:text-slate-200 hover:border-[#ff5200]/40 hover:text-[#ff5200] transition-colors">
                  ↻ Refresh from Block Capital
                </button>
                {(fa.blocks || []).length > 0 && (
                  <button type="button" onClick={() => setDepreciationChartOpen(o => !o)}
                    style={{ background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)' }}
                    className="px-4 py-2 rounded-lg text-white text-xs font-bold shadow-sm hover:shadow-md transition-shadow">
                    Depreciation Chart {depreciationChartOpen ? '▲' : '▼'}
                  </button>
                )}
              </div>
            } />

          {depreciationChartOpen && (() => {
            // Redesigned to match the CA's own Excel "Computation of
            // Depreciation" layout for a bank-submitted Project Report:
            // blocks run across as COLUMNS (with a Total column), years run
            // down as repeated Opening/Addition/Total/Less Dep/WDV row
            // groups — not one stacked mini-table per block. Row labels
            // (WDV year) are anchored to the case's actual base Financial
            // Year, same as the rest of the report.
            const years = capacityYearCount || caseData.projection_years || 6;
            const preview = computeDepreciationPreview(caseData, years)
              .slice().sort((x, y) => depBlockRank(x) - depBlockRank(y));
            if (preview.length === 0) return null;
            const dash = n => (!n ? '-' : fmt(n));
            const totalFor = (get, j) => preview.reduce((s, b) => s + (get(b.rows[j]) || 0), 0);
            const hasIdcp = preview.some(b => b.rows[0]?.additionIdcp);
            const hasSubsidy = preview.some(b => b.rows[0]?.lessSubsidy);
            const td = "text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200";
            const tdLabel = "px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200";
            return (
              <div className="mt-3 overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-900">
                      <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Description</th>
                      {preview.map((b, bi) => <th key={bi} className="text-right px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{b.name}</th>)}
                      <th className="text-right px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-extrabold text-slate-600 dark:text-slate-300 uppercase tracking-wide text-[10px]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={tdLabel}>Rate of Depreciation</td>
                      {preview.map((b, bi) => <td key={bi} className={td}>{b.sourceKey === 'landItems' ? '-' : `${b.rate ?? 0}%`}</td>)}
                      <td className={td}></td>
                    </tr>
                    {Array.from({ length: years }, (_, j) => (
                      <React.Fragment key={j}>
                        {j > 0 && <tr><td colSpan={preview.length + 2} className="py-1 border-b border-slate-100 dark:border-slate-800"></td></tr>}
                        {j === 0 && (
                          <tr>
                            <td className={tdLabel}>Opening Balance</td>
                            {preview.map((b, bi) => <td key={bi} className={td}>{dash(b.rows[0].openingBalance)}</td>)}
                            <td className={td}>{dash(totalFor(r => r.openingBalance, 0))}</td>
                          </tr>
                        )}
                        <tr>
                          <td className={tdLabel}>Addition during the year</td>
                          {preview.map((b, bi) => <td key={bi} className={td}>{dash(b.rows[j].addition)}</td>)}
                          <td className={td}>{dash(totalFor(r => r.addition, j))}</td>
                        </tr>
                        {j === 0 && hasIdcp && (
                          <tr>
                            <td className={tdLabel}>Additions - IDCP</td>
                            {preview.map((b, bi) => <td key={bi} className={td}>{dash(b.rows[0].additionIdcp)}</td>)}
                            <td className={td}>{dash(totalFor(r => r.additionIdcp, 0))}</td>
                          </tr>
                        )}
                        {j === 0 && hasSubsidy && (
                          <tr>
                            <td className={tdLabel}>Less: Subsidy Reduction</td>
                            {preview.map((b, bi) => <td key={bi} className={td}>{dash(b.rows[0].lessSubsidy)}</td>)}
                            <td className={td}>{dash(totalFor(r => r.lessSubsidy, 0))}</td>
                          </tr>
                        )}
                        <tr className="font-bold bg-orange-500/5">
                          <td className={tdLabel}>Total</td>
                          {preview.map((b, bi) => <td key={bi} className={td}>{dash(b.rows[j].total)}</td>)}
                          <td className={td}>{dash(totalFor(r => r.total, j))}</td>
                        </tr>
                        <tr>
                          <td className={tdLabel}>Less: Depreciation</td>
                          {preview.map((b, bi) => <td key={bi} className={td}>{dash(b.rows[j].depreciationAccounting)}</td>)}
                          <td className={td}>{dash(totalFor(r => r.depreciationAccounting, j))}</td>
                        </tr>
                        <tr className="font-bold bg-orange-500/10">
                          <td className={tdLabel}>WDV at the end of year {fyLabel(bp.baseFinancialYear, j + 1)}</td>
                          {preview.map((b, bi) => <td key={bi} className={td}>{dash(b.rows[j].netBlockAccounting)}</td>)}
                          <td className={td}>{dash(totalFor(r => r.netBlockAccounting, j))}</td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}

      {stepId === 'working_capital' && (
        <div>
          <StepHeader title="Working Capital" subtitle="Drives the DSCR and CMA working-capital forms" />
          <FundingBadge caseData={caseData} />
          <div className="max-w-sm mb-6">
            <Field label="Working Capital Limit Proposed" hint="Cash Credit / Overdraft limit requested — drives WC interest in the P&L"><NumberInput value={mof.wcLimit} onChange={v => setNested('means_of_finance', 'wcLimit', v)} /></Field>
          </div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Working Capital Assessment Method</div>
          <Field label="Method">
            <SelectInput value={wc.method || 'traditional'} onChange={v => setNested('wc_config', 'method', v)} options={[
              { value: 'traditional', label: 'Traditional Method' }, { value: 'turnover', label: 'Turnover Method' },
              { value: 'mpbf2', label: 'MPBF — 2nd Method of Lending (Tandon)' }, { value: 'mpbf3', label: 'MPBF — 3rd Method of Lending (Tandon)' },
              { value: 'negotiated', label: 'Negotiated Limit Method' },
            ]} />
          </Field>

          <div className="grid grid-cols-3 gap-x-5">
            <Field label="Debtor Period (days)"><NumberInput value={wc.debtorDays} onChange={v => setNested('wc_config', 'debtorDays', v)} /></Field>
            <Field label="Creditor Period (days)"><NumberInput value={wc.creditorDays} onChange={v => setNested('wc_config', 'creditorDays', v)} /></Field>
            <Field label="WC Interest Rate % p.a."><NumberInput value={wc.interestRatePct} onChange={v => setNested('wc_config', 'interestRatePct', v)} /></Field>
          </div>
          <div className="mb-4 text-[11px] text-slate-400 leading-relaxed">
            Closing Stock for Working Capital is the sum of two components — <b>Raw Material Stock</b> and <b>Finished Goods Stock</b> — both set as a "Closing Stock (Days)" column directly on the Capacity &amp; Raw Material step's Raw Material Inputs and Products &amp; By-Products tables respectively, not repeated here, so all stay in sync with the same figures used to build the case. Finished Goods is valued at each product's own selling price, including any by-product sitting in stock.
          </div>
          {wc.method === 'traditional' && (
            <div className="grid grid-cols-2 gap-x-5">
              <Field label="Stock Margin % (promoter)" hint="default 25"><NumberInput value={(wc.traditional || {}).stockMarginPct} onChange={v => set('wc_config', { ...wc, traditional: { ...(wc.traditional || {}), stockMarginPct: v } })} /></Field>
              <Field label="Debtor Margin % (promoter)" hint="default 40"><NumberInput value={(wc.traditional || {}).debtorMarginPct} onChange={v => set('wc_config', { ...wc, traditional: { ...(wc.traditional || {}), debtorMarginPct: v } })} /></Field>
            </div>
          )}
          {wc.method === 'turnover' && (
            <div className="grid grid-cols-2 gap-x-5">
              <Field label="% of Projected Turnover" hint="default 20"><NumberInput value={(wc.turnover || {}).pctOfTurnover} onChange={v => set('wc_config', { ...wc, turnover: { ...(wc.turnover || {}), pctOfTurnover: v } })} /></Field>
              <Field label="Promoter Margin %" hint="default 25 (Tandon: min. 25% of current assets from long-term sources)"><NumberInput value={(wc.turnover || {}).marginPct} onChange={v => set('wc_config', { ...wc, turnover: { ...(wc.turnover || {}), marginPct: v } })} /></Field>
            </div>
          )}
          {(wc.method === 'mpbf2' || wc.method === 'mpbf3') && (
            <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 mt-1">
              MPBF (Tandon Committee, Method 2/3) computed from the standard published formula — validate against your bank's actual sanction letter, since we don't have a real sample case to test it against.
            </p>
          )}
          {wc.method === 'negotiated' && (
            <p className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-900/40 rounded-lg px-3 py-2 mt-1">
              Uses the "Working Capital Limit Proposed" field above as the Bank Finance figure — flat across every projected year, exactly as a bank sanction actually works. Promoter Margin is whatever's left of Net WC Required (Stock + Debtors − Creditors) once that limit is subtracted, growing each year as the operating cycle grows.
            </p>
          )}

          {(() => {
            const wcPreview = computeWorkingCapitalPreview(caseData);
            const years = wcPreview.byYear;
            if (!years.length) return null;
            return (
              <div className="mt-4">
                <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-900">
                        <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">Particulars</th>
                        {years.map((r, i) => <th key={i} className="text-right px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{fyLabel(bp.baseFinancialYear, r.year)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Current Assets (Stock + Debtors)', get: r => r.stock + r.debtors },
                        { label: 'Less: Creditors', get: r => r.creditors },
                        { label: 'Bank Finance (MPBF)', get: r => r.mpbf, bold: true },
                        { label: 'Promoter Margin', get: r => r.promoterMargin, bold: true },
                      ].map((row, i) => (
                        <tr key={i} className={row.bold ? 'font-bold bg-orange-500/5' : ''}>
                          <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{row.label}</td>
                          {years.map((r, j) => <td key={j} className="text-right px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200">{fmt(row.get(r))}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-3 gap-x-5 mt-4">
            <Field label="Projection Years" tooltip="Set by the number of rows in the Capacity & Raw Material step's Annual Capacity table — add or remove a year there, not here, so every schedule (Raw Material Procurement, P&L, Balance Sheet, Cash Flow) stays on the same year count.">
              <div className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 text-sm text-slate-500 dark:text-slate-400 flex items-center">{caseData.projection_years || '—'}</div>
            </Field>
            <Field label="Tax Rate %"><NumberInput value={caseData.tax_rate_pct} onChange={v => set('tax_rate_pct', v)} /></Field>
          </div>

          <Card className="p-4 mt-5 mb-2 bg-slate-50/60 dark:bg-slate-900/40">
            <div className="text-[11px] font-extrabold text-[#001254] dark:text-slate-200 uppercase tracking-wide mb-2.5">Which Method — and Why</div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-3 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              <div><b className="text-slate-700 dark:text-slate-200">Traditional Method</b> — build up Stock (RM+FG) and Debtors from the operating cycle, apply a separate margin % to each (Stock 25%, Debtors 40% here) — the bank finances the rest. Oldest, least standardized; margin varies bank-to-bank.</div>
              <div><b className="text-slate-700 dark:text-slate-200">Turnover Method (Nayak Committee)</b> — for smaller/MSME borrowers. No stock/debtor build-up needed: WC = 20% of projected turnover, bank finances 80% of that, promoter margin = 5% of turnover. Simple, formula-only.</div>
              <div><b className="text-slate-700 dark:text-slate-200">MPBF Method II (Tandon)</b> — WC Gap = Current Assets − Other Current Liabilities. Bank finances 75% of that Gap; promoter brings 25% as ONE blended margin (not per asset class). What most bank CMA/Form II formats are built around.</div>
              <div><b className="text-slate-700 dark:text-slate-200">MPBF Method III (Tandon)</b> — same 75:25 as Method II, but first carves out "Core Current Assets" (the permanent minimum stock the business always carries) and funds that portion entirely from long-term sources, not short-term revolving credit.</div>
              <div><b className="text-slate-700 dark:text-slate-200">Negotiated Limit Method</b> — the CA proposes one flat CC/OD limit ("Working Capital Limit Proposed" above), same figure every projected year, exactly as a real bank sanction works. Promoter Margin is simply Net WC Required minus that limit, growing each year as the gap widens. Common in real bank-submitted DPRs.</div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              <b className="text-slate-700 dark:text-slate-200">Cash Credit (CC) vs Overdraft (OD):</b> both are ways a bank actually disburses the Working Capital Limit above — CC is financed against hypothecation of stock &amp; book debts, with Drawing Power tracked via periodic stock statements (the standard manufacturing/trading facility); OD lets the current account run into a debit balance up to the sanctioned limit, usually secured by FD/property/general standing rather than tracked stock — simpler, common for services/professionals where DP tracking isn't practical. The MPBF figure above is the ceiling either facility gets sanctioned against.
            </div>
          </Card>

          <StepFooter stepIndex={stepIndex} onBack={goBack} onSave={goSave} onNext={goNext} isLastStep={isLastStep} saving={saving} />
        </div>
      )}
    </div>
  );
}
